import { Box, StatusBadge } from "@bcc-ui/ui-components";
import ReactJsonView from "@microlink/react-json-view";
import classNames from "classnames";
import { useFeature } from "flagged";
import { TFunction } from "i18next";
import * as React from "react";
import { useTranslation } from "react-i18next";

import style from "./endpoints-view.scss";
import ApiPlainCopy from "components/icons/ApiPlainCopy";
import WhiteCopyIcon from "components/icons/WhiteCopyIcon";
import { ApiMethodBadge } from "components/support/api-guide/endpoints/ApiMethodBadge";
import endpointStyle from "components/support/api-guide/endpoints/endpoints-view.scss";
import StaticTable from "components/support/api-guide/StaticTable";
import Heading from "components/typography/heading/Heading";
import { TextBlock } from "components/typography/textBlock/TextBlock";
import { deliveryTypes } from "domain/licenses";
import { MAX_COLUMN_COUNT } from "domain/reports";
import { FLAG_ASSET_MANAGEMENT } from "services/feature/FeatureFlagService";
import { getPublicApiUrl } from "services/login/endpointRepository";
import { isUserParentInternal } from "services/tenants/tenantCookieService";

import checkMark from "assets/images/icons/checkMark.svg";

import testIds from "testIds.json";

const API_KEY_HEADER = `-H "X-BLANCCO-API-KEY: 8574e755-1f80-4af4-b7ae-a8187afb3909"`;
const BEARER_TOKEN_HEADER = `-H "Authorization: Bearer eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJhOTQ0ZjE4ZS1jZDBiLTQ5ZTYtOGQ0Mi0yYzY4ZGI3MzE1NDYiLCJ0ZW5hbnRfdXVpZCI6ImZlNDNjNDM5LWQwZDktNDY1Yi04MTRhLTg2NmE2ZDkyZGNjYyIsImlzcyI6ImJsYW5jY28tcHVibGljLWFwaSIsImF1ZCI6ImJsYW5jY28tcHVibGljLWFwaSIsImlhdCI6MTczNTY4OTYwMCwiZXhwIjoxNzM1NjkzMjAwfQ.ZXhhbXBsZS1zaWduYXR1cmUtbm90LWEtcmVhbC10b2tlbi1kby1ub3QtdXNl"`;
const COMMAND_PART_CURL = "curl";
const COMMAND_PART_DATA = "-d";
const COMMAND_PART_XDELETE = "-XDELETE";
const COMMAND_PART_XPOST = "-XPOST";
const CONTENT_TYPE_JSON_HEADER = `-H "Content-Type: application/json"`;
const CONTENT_TYPE_XML_HEADER = `-H "Content-Type: application/xml"`;
const INDENT = "    ";
const LANGUAGES = ["DE_DE", "EN_US", "ES_ES", "FR_FR", "JA_JP", "KO_KR", "PT_BR", "ZH_CN"];
const PUBLIC_LINK_LANGUAGES = [
    "DE_DE",
    "EN_US",
    "ES_ES",
    "FR_FR",
    "HU_HU",
    "IT_IT",
    "JA_JP",
    "KO_KR",
    "PL_PL",
    "PT_BR",
    "RU_RU",
    "SK_SK",
    "ZH_CN",
    "ZH_TW",
];
const MAX_PUBLIC_LINK_REPORT_COUNT = 1000;
const MIN_COLUMN_COUNT = 1;
const CSV_SUMMARY_PDF_EXAMPLE_COLUMNS = [
    "report.report_date",
    "report.uuid",
    "report.product_name",
    "blancco_hardware_report.system.imei",
    "blancco_software_report.operating_system.version",
    "user_data.fields.part ID",
]
    .map((each) => `"${each}"`)
    .join(", ");
const MIN_REPORT_COUNT = 1;
const MAX_REPORT_COUNT = 10;
const DEFAULT_REPORT_COUNT = 100;
const DEFAULT_PDF_REPORT_COUNT = 40;
const DEFAULT_COMBINED_PDF_REPORT_COUNT = 50;

interface Endpoint {
    linkId: string;
    title: string;
    introduction: string;
    details?: string;
    url: string;
    method: string;
    format: string;
    requestExample?: Record<string, unknown>;
    responseExample: JSX.Element;
    curlSection: JSX.Element;
    parameterSection?: JSX.Element;
    description?: string;
    mainTitle?: string;
    internal?: boolean;
}

interface RequestParameter {
    name: string;
    required: boolean;
    internal?: boolean;
    description: string;
}

enum CommandPartType {
    PLAINTEXT,
    JSON,
}

interface CollapsibleBoxProps {
    title: React.ReactNode;
    method?: React.ReactNode;
    internal?: boolean;
    children: React.ReactNode;
}

class CommandPart {
    value: string;
    type: CommandPartType;
    indent: boolean;

    constructor(value: string, type: CommandPartType, indent = true) {
        this.type = type;
        this.value = value;
        this.indent = indent;
    }
}

const createPlainTextCommandPart = (value: string, indent?: boolean) => {
    return new CommandPart(value, CommandPartType.PLAINTEXT, indent);
};

const createJsonCommandPart = (value: string, indent?: boolean) => {
    return new CommandPart(value, CommandPartType.JSON, indent);
};

function createUrlSection(t: TFunction, endpoint: Endpoint): JSX.Element {
    function createContent(label: string, value: string, copyClickHandler?: () => void): JSX.Element {
        let copyDiv = null;
        if (copyClickHandler != null) {
            copyDiv = (
                <div onClick={copyClickHandler} className={style.urlSubItem}>
                    <ApiPlainCopy />
                </div>
            );
        }
        return (
            <>
                <div className={classNames(style.urlTitle, style.urlSubItem)}>{label}</div>
                <div className={classNames(style.urlItemValueContainer, style.urlSubItem)}>{value}</div>
                {copyDiv}
            </>
        );
    }

    function copyUrl() {
        navigator.clipboard.writeText(endpoint.url);
    }

    const items = [
        createContent(t("Support.apiGuide.url"), endpoint.url, copyUrl),
        createContent(t("Support.apiGuide.httpMethod"), endpoint.method),
        createContent(t("Support.apiGuide.format"), endpoint.format),
    ];
    return (
        <div className={style.urlSection}>
            {items.map((each, index) => (
                <div key={index} className={style.urlItem}>
                    {each}
                </div>
            ))}
        </div>
    );
}

function createTextExample(title: string, text: string): JSX.Element {
    return (
        <div className={style.textExample}>
            <div className={style.textHeader}>
                <div className={style.textTitle}>{title}</div>
                <span onClick={() => navigator.clipboard.writeText(text)} className={style.textExampleCopyIcon}>
                    <ApiPlainCopy />
                </span>
            </div>
            <div className={style.exampleTextContainer}>{text}</div>
        </div>
    );
}

function createJsonExample(title: string, json: Record<string, unknown>, isResponse: boolean, sortKeys = true) {
    return (
        <div className={classNames(style.curlExample, { [style.textExample]: isResponse })}>
            <div className={classNames(style.curlHeader, { [style.textHeader]: isResponse })}>
                <div className={classNames(style.curlTitle, { [style.textTitle]: isResponse })}>{title}</div>
                <span
                    onClick={() => navigator.clipboard.writeText(JSON.stringify(json, null, 2))}
                    className={classNames(style.textExampleCopyIcon, { [style.curlExampleCopyIcon]: !isResponse })}
                >
                    {isResponse ? <ApiPlainCopy /> : <WhiteCopyIcon />}
                </span>
            </div>
            <div className={style.scrollableTable}>
                <div
                    className={classNames({
                        [style.exampleResponseJsonContainer]: isResponse,
                        [style.exampleJsonContainer]: !isResponse,
                    })}
                >
                    <ReactJsonView
                        collapsed={false}
                        displayArrayKey={false}
                        displayDataTypes={false}
                        displayObjectSize={false}
                        indentWidth={4}
                        name={false}
                        sortKeys={sortKeys}
                        src={json}
                    />
                </div>
            </div>
        </div>
    );
}

function createBodyExamples(t: TFunction, endpoint: Endpoint): JSX.Element {
    return (
        <div>
            <div className={style.exampleSection}>
                {endpoint.requestExample && (
                    <div>{createJsonExample(t("Support.apiGuide.requestExample"), endpoint.requestExample, false)}</div>
                )}
            </div>
            {endpoint.responseExample}
        </div>
    );
}

const minifyJson = (postBody: string) => {
    const withoutQuotes = postBody.trim().replace(/^'/, "").replace(/'$/, "");
    return singleQuote(JSON.stringify(JSON.parse(withoutQuotes)));
};

const indentJson = (value: string) => {
    return value
        .split("\n")
        .map((line) => INDENT + line)
        .join("\n");
};

function renderCurlBlock(title: string, testId: string, commandParts: CommandPart[]): JSX.Element {
    const copiedCommand = commandParts
        .map((part) => (part.type === CommandPartType.JSON ? minifyJson(part.value) : part.value))
        .join(" ");

    const displayedCommand = commandParts
        .map((part) => {
            return part.indent
                ? part.type === CommandPartType.PLAINTEXT
                    ? INDENT + part.value
                    : indentJson(part.value)
                : part.value;
        })
        .join("\n");
    return (
        <div className={style.curlExample}>
            <div className={style.curlHeader}>
                <div className={style.curlTitle}>{title}</div>

                <span
                    onClick={() => navigator.clipboard.writeText(copiedCommand)}
                    className={style.curlExampleCopyIcon}
                >
                    <WhiteCopyIcon />
                </span>
            </div>
            <textarea
                readOnly={true}
                wrap={"hard"}
                value={displayedCommand}
                data-resize={"true"}
                data-testid={testId}
            ></textarea>
        </div>
    );
}

function createCurlSection(t: TFunction, commandParts: CommandPart[]): JSX.Element {
    const apiKeyPartIndex = commandParts.findIndex((part) => part.value === API_KEY_HEADER);
    const bearerTokenParts = [...commandParts];
    bearerTokenParts[apiKeyPartIndex] = createPlainTextCommandPart(
        BEARER_TOKEN_HEADER,
        commandParts[apiKeyPartIndex].indent
    );
    return (
        <>
            {renderCurlBlock(
                t("Support.apiGuide.curlExampleApiKey"),
                testIds.workArea.support.apiGuide.endpoint.curlExampleTextArea,
                commandParts
            )}
            {renderCurlBlock(
                t("Support.apiGuide.curlExampleBearerToken"),
                testIds.workArea.support.apiGuide.endpoint.curlExampleBearerTokenTextArea,
                bearerTokenParts
            )}
        </>
    );
}

function createParameterSection(
    t: TFunction,
    urlParameters: Array<RequestParameter> = [],
    bodyParameters: Array<RequestParameter> = []
): JSX.Element | undefined {
    const missingUrls = urlParameters.length == 0;
    const missingBodies = bodyParameters.length == 0;
    if (missingUrls && missingBodies) {
        return;
    }

    const headers = [
        { className: style.parameterColumn, value: t("Support.apiGuide.parameterTable.header.parameter") },
        { className: style.requiredColumn, value: t("Support.apiGuide.parameterTable.header.required") },
        { value: t("Support.apiGuide.parameterTable.header.description") },
    ];

    const cellMapper = (each: RequestParameter) => [
        each.internal ? (
            <span className={style.parameterTd}>
                {each.name}
                <StatusBadge value={t("Common.internal")} status={4} />
            </span>
        ) : (
            each.name
        ),
        each.required ? <img src={checkMark} alt={t("AltText.checkmark")} /> : null,
        each.description,
    ];

    const urlPart = !missingUrls && (
        <div className={style.urlAndParameterPart}>
            <Heading tag={"div"} variant="SUBTITLE_1">
                {t("Support.apiGuide.urlRequestParameters")}
            </Heading>
            <StaticTable headers={headers} cells={urlParameters.map(cellMapper)} />{" "}
        </div>
    );
    const bodyPart = !missingBodies && (
        <div className={style.scrollableTable}>
            <Heading tag={"div"} variant="SUBTITLE_1">
                {t("Support.apiGuide.bodyRequestParameters")}
            </Heading>
            <StaticTable headers={headers} cells={bodyParameters.map(cellMapper)} />
        </div>
    );
    return (
        <div>
            {urlPart}
            {bodyPart}
        </div>
    );
}

function listLanguages(languages = LANGUAGES): string {
    return languages.map((each) => `"${each}"`).join(", ");
}

const INTERNAL_FIELDS = new Set([
    "salesforce_contract_id",
    "salesforce_contact_email",
    "salesforce_contract_state",
    "salesforce_contract_end_date",
    "salesforce_lead_id",
    "salesforce_id",
    "ems",
    "opportunity_id",
    "case_number",
    "rate_uuid",
]);

function createExportReportWithUuidEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const languageListing = listLanguages();
    return {
        linkId: "export-single-report",
        title: t("Support.apiGuide.endpoint.exportSingleReport.title"),
        introduction: t("Support.apiGuide.endpoint.exportSingleReport.introduction"),
        url: baseUrl,
        method: "GET",
        format: t("Common.na"),
        responseExample: createTextExample(
            t("Support.apiGuide.response"),
            t("Support.apiGuide.endpoint.exportSingleReport.responseDescription")
        ),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(baseUrl.replace("{uuid}", "4715f169-1229-4315-90b4-c125a031e865")),
        ]),
        parameterSection: createParameterSection(t, [
            {
                name: "uuid",
                required: true,
                description: t("Support.apiGuide.endpoint.exportSingleReport.uuidParameter"),
            },
            {
                name: "category",
                required: false,
                description: t("Support.apiGuide.endpoint.exportSingleReport.categoryParameter"),
            },
            {
                name: "format",
                required: false,
                description: t("Support.apiGuide.endpoint.exportSingleReport.formatParameter"),
            },
            {
                name: "language",
                required: false,
                description: t("Support.apiGuide.endpoint.exportSingleReport.languageParameter", {
                    languages: languageListing,
                }),
            },
            {
                name: "template_uuid",
                required: false,
                description: t("Support.apiGuide.endpoint.exportSingleReport.templateUuidParameter"),
            },
        ]),
        description: t("Support.apiGuide.paths.reportTypeDescription"),
        mainTitle: t("Support.apiGuide.paths.reportTypeTitle"),
    };
}

function createExportReportGetEndpoint(t: TFunction, baseUrl: string, cursor: RequestParameter): Endpoint {
    // without IMEI filtering. Add them if there's time.
    return {
        linkId: "export-reports-get",
        title: t("Support.apiGuide.endpoint.exportReportsGet.title"),
        introduction: t("Support.apiGuide.endpoint.exportReportsGet.introduction"),
        url: baseUrl,
        method: "GET",
        format: t("Common.na"),
        responseExample: createTextExample(t("Support.apiGuide.response"), t("Support.apiGuide.zipResponse")),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(`${baseUrl}?@imei=355324081989399`),
        ]),
        parameterSection: createParameterSection(t, [
            {
                name: "@imei",
                required: false,
                description: t("Support.apiGuide.endpoint.exportReportsGet.imeiFilterDescription"),
            },
            {
                name: "category",
                required: false,
                description: t("Support.apiGuide.endpoint.exportReportsGet.categoryParameter"),
            },
            {
                name: "language",
                required: false,
                description: t("Support.apiGuide.endpoint.exportReportsGet.languageParameter", {
                    languages: listLanguages(),
                }),
            },
            {
                name: "container",
                required: false,
                description: t("Support.apiGuide.endpoint.exportReportsGet.containerParameter"),
            },
            {
                name: "format",
                required: false,
                description: t("Support.apiGuide.endpoint.exportReportsGet.formatParameter"),
            },
            {
                name: "template_uuid",
                required: false,
                description: t("Support.apiGuide.endpoint.exportReportsGet.templateUuidParameter"),
            },
            cursor,
        ]),
    };
}

const singleQuote = (value: string) => {
    return "'" + value + "'";
};

function createExportReportPostEndpoint(t: TFunction, baseUrl: string, cursor: RequestParameter): Endpoint {
    const postCurlExample = {
        filter: { date: { gt: "2021-12-31T23:59:59Z" }, fields: [{ like: "355324081989399", name: "@imei" }] },
    };
    const filterParameters = [
        {
            name: "category",
            required: false,
            description: t("Support.apiGuide.endpoint.exportReportsPost.parameterDescription.category"),
        },
        {
            name: "format",
            required: false,
            description: t("Support.apiGuide.endpoint.exportReportsPost.parameterDescription.format"),
        },
        {
            name: "container",
            required: false,
            description: t("Support.apiGuide.endpoint.exportReportsPost.parameterDescription.container"),
        },
        {
            name: "filter",
            required: false,
            description: t("Support.apiGuide.endpoint.exportReportsPost.parameterDescription.filter"),
        },
        {
            name: "filter.date",
            required: false,
            description: t("Support.apiGuide.endpoint.exportReportsPost.parameterDescription.date"),
        },
        {
            name: "filter.fields",
            required: false,
            description: t("Support.apiGuide.endpoint.exportReportsPost.parameterDescription.fields"),
        },
        {
            name: "filter.options.logic",
            required: false,
            description: t("Support.apiGuide.endpoint.exportReportsPost.parameterDescription.logic"),
        },
        {
            name: "csv.columns",
            required: false,
            description: t("Support.apiGuide.endpoint.exportReportsPost.parameterDescription.columns", {
                columnCountMin: MIN_COLUMN_COUNT,
                columnCountMax: MAX_COLUMN_COUNT,
                exampleColumns: `[${CSV_SUMMARY_PDF_EXAMPLE_COLUMNS}]`,
            }),
        },
        {
            name: "csv.public_link_expiration",
            required: false,
            description: t("Support.apiGuide.endpoint.exportReportsPost.parameterDescription.csvPublicLinkExpiration"),
        },
        {
            name: "summary.columns",
            required: false,
            description: t("Support.apiGuide.endpoint.exportReportsPost.parameterDescription.summaryColumns", {
                columnCountMin: MIN_COLUMN_COUNT,
                columnCountMax: MAX_COLUMN_COUNT,
                exampleColumns: `[${CSV_SUMMARY_PDF_EXAMPLE_COLUMNS}]`,
            }),
        },
        {
            name: "summary.public_link_expiration",
            required: false,
            description: t(
                "Support.apiGuide.endpoint.exportReportsPost.parameterDescription.summaryPublicLinkExpiration"
            ),
        },
        {
            name: "size",
            required: false,
            description: t("Support.apiGuide.endpoint.exportReportsPost.parameterDescription.size", {
                reportCountMin: MIN_REPORT_COUNT,
                reportCountMax: MAX_REPORT_COUNT,
                reportCountDefault: DEFAULT_REPORT_COUNT,
                pdfReportCountDefault: DEFAULT_PDF_REPORT_COUNT,
                combinePdfDefaultCount: DEFAULT_COMBINED_PDF_REPORT_COUNT,
            }),
        },
        {
            name: "sort_by",
            required: false,
            description: t("Support.apiGuide.endpoint.exportReportsPost.parameterDescription.sortBy"),
        },
        {
            name: "sort_direction",
            required: false,
            description: t("Support.apiGuide.endpoint.exportReportsPost.parameterDescription.sortDirection"),
        },
        {
            name: "language",
            required: false,
            description: t("Support.apiGuide.endpoint.exportReportsPost.parameterDescription.language", {
                languages: listLanguages(),
            }),
        },
        {
            name: "template_uuid",
            required: false,
            description: t("Support.apiGuide.endpoint.exportReportsPost.parameterDescription.templateUuid"),
        },
    ];
    return {
        linkId: "export-reports-post",
        title: t("Support.apiGuide.endpoint.exportReportsPost.title"),
        introduction: t("Support.apiGuide.endpoint.exportReportsPost.introduction"),
        url: baseUrl,
        method: "POST",
        format: "application/json",
        requestExample: {
            cursor: "",
            filter: {
                category: "ERASURE",
                date: { gt: "2021-12-31T23:59:59-0500" },
                fields: [{ name: "@imei", like: "355324081989399" }],
            },
        },
        responseExample: createTextExample(t("Support.apiGuide.response"), t("Support.apiGuide.zipResponse")),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_JSON_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA),
            createJsonCommandPart(singleQuote(JSON.stringify(postCurlExample, null, 4))),
            createPlainTextCommandPart(baseUrl),
        ]),
        parameterSection: createParameterSection(t, [cursor], [cursor, ...filterParameters]),
    };
}

function createReportPublicLinksEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const requestExample = {
        report_uuids: ["a1b2c3d4-0000-0000-0000-000000000001", "a1b2c3d4-0000-0000-0000-000000000002"],
        expiration: "2030-12-31",
        language: "EN_US",
    };
    const responseExample = {
        expiration: "2030-12-31",
        links: [
            {
                report_uuid: "a1b2c3d4-0000-0000-0000-000000000001",
                link: "https://example.com/reports/public?token=<token>",
            },
            {
                report_uuid: "a1b2c3d4-0000-0000-0000-000000000002",
                link: "https://example.com/reports/public?token=<token>",
            },
        ],
    };
    return {
        linkId: "report-public-links-post",
        title: t("Support.apiGuide.endpoint.reportPublicLinksPost.title"),
        introduction: t("Support.apiGuide.endpoint.reportPublicLinksPost.introduction"),
        url: baseUrl,
        method: "POST",
        format: "application/json",
        requestExample: requestExample,
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true, false),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_JSON_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA),
            createJsonCommandPart(singleQuote(JSON.stringify(requestExample, null, 4))),
            createPlainTextCommandPart(baseUrl),
        ]),
        parameterSection: createParameterSection(
            t,
            [],
            [
                {
                    name: "report_uuids",
                    required: true,
                    description: t("Support.apiGuide.endpoint.reportPublicLinksPost.parameterDescription.reportUuids", {
                        reportCountMax: MAX_PUBLIC_LINK_REPORT_COUNT,
                    }),
                },
                {
                    name: "expiration",
                    required: true,
                    description: t("Support.apiGuide.endpoint.reportPublicLinksPost.parameterDescription.expiration"),
                },
                {
                    name: "language",
                    required: false,
                    description: t("Support.apiGuide.endpoint.reportPublicLinksPost.parameterDescription.language", {
                        languages: listLanguages(PUBLIC_LINK_LANGUAGES),
                    }),
                },
            ]
        ),
    };
}

function createImportReportsPublicApiEndpoint(t: TFunction, baseUrl: string): Endpoint {
    return {
        linkId: "import-reports-post",
        title: t("Support.apiGuide.endpoint.importReportPost.title"),
        introduction: t("Support.apiGuide.endpoint.importReportPost.introduction"),
        url: baseUrl,
        method: "POST",
        format: "application/xml",
        responseExample: createJsonExample(
            t("Support.apiGuide.response"),
            {
                result: {
                    reports: {
                        total: 2,
                        success: {
                            total: 2,
                        },
                        failure: {
                            total: 0,
                        },
                    },
                },
            },
            true
        ),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_XML_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA + ` '@blancco_report.xml'`),
            createPlainTextCommandPart(baseUrl),
        ]),
    };
}

function createAutopilotRecheckEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const requestExample = {
        report_uuids: [
            "a1b2c3d4-0000-0000-0000-000000000001",
            "a1b2c3d4-0000-0000-0000-000000000002",
            "a1b2c3d4-0000-0000-0000-000000000003",
        ],
    };
    const responseExample = {
        results: [
            {
                report_uuid: "a1b2c3d4-0000-0000-0000-000000000001",
                serial_number: "SN-ABC123",
                status: "INITIATED",
            },
            {
                report_uuid: "a1b2c3d4-0000-0000-0000-000000000002",
                serial_number: "SN-DEF456",
                status: "SKIPPED",
            },
            {
                report_uuid: "a1b2c3d4-0000-0000-0000-000000000003",
                serial_number: null,
                status: "NOT_FOUND",
            },
        ],
    };
    return {
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_JSON_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA),
            createJsonCommandPart(singleQuote(JSON.stringify(requestExample, null, 4))),
            createPlainTextCommandPart(baseUrl),
        ]),
        format: "application/json",
        introduction: t("Support.apiGuide.endpoint.autopilotRecheckPost.introduction"),
        linkId: "autopilot-recheck-post",
        method: "POST",
        requestExample: requestExample,
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true, false),
        title: t("Support.apiGuide.endpoint.autopilotRecheckPost.title"),
        url: baseUrl,
        parameterSection: createParameterSection(
            t,
            [],
            [
                {
                    name: "report_uuids",
                    required: true,
                    description: t("Support.apiGuide.endpoint.autopilotRecheckPost.parameterDescription.reportUuids"),
                },
            ]
        ),
    };
}

function createCompareReportsEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const requestExample = {
        compared_paths: ["blancco_hardware_report.disks.disk.serial"],
        match_path: "blancco_hardware_report.system.serial",
    };
    const newReportUuid = "cdf8013d-d831-4497-813d-a4ef142da405";
    return {
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_JSON_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA),
            createJsonCommandPart(singleQuote(JSON.stringify(requestExample, null, 4))),
            createPlainTextCommandPart(baseUrl.replace("{uuid}", newReportUuid)),
        ]),
        format: "application/json",
        introduction: t("Support.apiGuide.endpoint.compareReportsPost.introduction"),
        linkId: "compare-reports-post",
        method: "POST",
        requestExample: requestExample,
        responseExample: createJsonExample(
            t("Support.apiGuide.response"),
            {
                comparison: {
                    success: { code: "OK" },
                    report_uuids: [newReportUuid, "f64303a8-de32-4b20-857e-c4abbb2497d4"],
                    mismatch: {
                        found: true,
                        mismatches: [
                            {
                                path: "blancco_hardware_report.disks.disk.serial",
                                old_value: "WD-313",
                                new_value: "WD-131",
                            },
                        ],
                    },
                },
            },
            true
        ),
        title: t("Support.apiGuide.endpoint.compareReportsPost.title"),
        url: baseUrl,
        parameterSection: createParameterSection(
            t,
            [
                {
                    name: "uuid",
                    required: true,
                    description: t("Support.apiGuide.endpoint.compareReportsPost.parameterDescription.uuid"),
                },
            ],
            [
                {
                    name: "compared_paths",
                    required: true,
                    description: t("Support.apiGuide.endpoint.compareReportsPost.parameterDescription.comparedPaths"),
                },
                {
                    name: "match_path",
                    required: true,
                    description: t("Support.apiGuide.endpoint.compareReportsPost.parameterDescription.matchPath"),
                },
                {
                    name: "email_notification",
                    required: false,
                    description: t(
                        "Support.apiGuide.endpoint.compareReportsPost.parameterDescription.emailNotification"
                    ),
                },
            ]
        ),
    };
}

function createImportWorkflowPostEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const requestExample = {
        tenant_uuid: "bd9784f2-1668-453a-a38f-fbf1500c7339",
        active: false,
        workflow: {
            autoStart: true,
            description: "",
            editorMetadata: {
                activeItem: -1,
                editorGeneration: "V2",
                editorVersion: "7.18.0",
                height: 20000,
                profile: "bde",
                type: "workflow",
                width: 25000,
                x: -1723,
                y: -1797,
            },
            embeddedDocuments: [],
            name: "test12389",
            objects: [
                {
                    editorData: {
                        x: 2500,
                        y: 2200,
                    },
                    itemActionType: "Booted",
                    itemAtFail: -1,
                    itemAtOk: 1,
                    itemBriefText: "Start",
                    itemType: "Booted",
                    item_id: 0,
                    x: 2500,
                    y: 2200,
                },
                {
                    editorData: {
                        x: 2800,
                        y: 2200,
                    },
                    itemActionType: "Finalize",
                    itemAtFail: -1,
                    itemAtOk: -1,
                    itemBriefText: "Finish",
                    itemType: "Finalize",
                    item_id: 1,
                    showFail: false,
                    systemRestart: false,
                    systemShutdown: false,
                    x: 2800,
                    y: 2200,
                },
            ],
            reportToXml: true,
            schemaVersion: "1.0.0",
            startItem: "Booted",
            version: "7.18.0",
            workAreaHeight: 20000,
            workAreaWidth: 25000,
        },
    };
    const responseExample = {
        uuid: "490e7ab9-2d39-4475-8843-f648799ed2fb",
        modified: "2025-04-21T09:59:30Z",
        modified_by: "xyz.abc@blancco.com",
        missing_sub_workflows: ["Workflow1"],
    };
    return {
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_JSON_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA),
            createJsonCommandPart(singleQuote(JSON.stringify(requestExample, null, 4))),
            createPlainTextCommandPart(baseUrl),
        ]),
        format: "application/json",
        introduction: t("Support.apiGuide.endpoint.importSingleWorkflowPost.introduction"),
        linkId: "import-workflow-post",
        method: "POST",
        requestExample: requestExample,
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        title: t("Support.apiGuide.endpoint.importSingleWorkflowPost.title"),
        url: baseUrl,
        parameterSection: createParameterSection(t, [
            {
                name: "tenant_uuid",
                required: false,
                description: t("Support.apiGuide.endpoint.importSingleWorkflowPost.parameterDescription.tenant_uuid"),
            },
            {
                name: "active",
                required: false,
                description: t("Support.apiGuide.endpoint.importSingleWorkflowPost.parameterDescription.active"),
            },
            {
                name: "workflow",
                required: true,
                description: t("Support.apiGuide.endpoint.importSingleWorkflowPost.parameterDescription.workflow"),
            },
        ]),
        mainTitle: t("Support.apiGuide.paths.workflowTypeTitle"),
        description: t("Support.apiGuide.paths.workflowTypeDescription"),
    };
}

function createExportWorkflowPostEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const requestExample = {
        tenant_uuid: "cdf8013d-d831-4497-813d-a4ef142da405",
    };
    return {
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_JSON_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA),
            createJsonCommandPart(singleQuote(JSON.stringify(requestExample, null, 4))),
            createPlainTextCommandPart(baseUrl.replace("{uuid}", "hgt8013d-d831-4497-813d-a4ef142da467")),
        ]),
        format: "application/json",
        introduction: t("Support.apiGuide.endpoint.exportSingleWorkflowPost.introduction"),
        linkId: "export-workflow-post",
        method: "POST",
        requestExample: requestExample,
        responseExample: createTextExample(
            t("Support.apiGuide.response"),
            t("Support.apiGuide.endpoint.exportSingleWorkflowPost.responseDescription")
        ),
        title: t("Support.apiGuide.endpoint.exportSingleWorkflowPost.title"),
        url: baseUrl,
        parameterSection: createParameterSection(t, [
            {
                name: "tenant_uuid",
                required: false,
                description: t("Support.apiGuide.endpoint.exportSingleWorkflowPost.parameterDescription.tenant_uuid"),
            },
        ]),
    };
}

function createFetchAllWorkflowPostEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const requestExample = {
        tenant_uuid: "cdf8013d-d831-4497-813d-a4ef142da405",
        search: "",
        cursor: "",
        product: "",
        version: "",
        from: "",
        to: "",
    };
    const responseExample = {
        workflows: [
            {
                workflow: null,
                name: "A simple workflow",
                uuid: "a176682d-99d3-49b5-b36a-183bdbad689b",
                user_name: "jane.doe@example.com",
                user_uuid: "2bdcb6a8-4014-498e-bb33-7eb8e1d4f801",
                created_date: "2025-07-24T11:50:56Z",
                modified_date: "2025-07-24T12:02:56Z",
                profile: "BMP",
                description: "",
                modified_by_user_name: "jane.doe@example.com",
                editor_version: "1.0.0",
                enabled: true,
                editor_generation: "V2",
                connected_workflows: {
                    parent_workflows: ["workflow22", "workflow23"],
                    sub_workflows: [],
                    run_diagnose_workflows: [],
                    run_process_flow_workflows: [],
                    grading_panel_workflows: [],
                    collect_workflows: [],
                    remote_workflow_count: 0,
                    all_sub_workflows: [],
                },
                sequence_id: 87744,
                modified_by_user_uuid: "2bdcb6a8-4014-498e-bb33-7eb8e1d4f801",
                own: false,
                launch_source: "ALL",
                default_workflow: {
                    own: false,
                    other: {
                        count: 0,
                        userDetails: [],
                    },
                },
            },
        ],
        cursor: "OTk3OTNlNDMtZWE0NC00Mjk1LTllZGQtNmNkOTVlZTc1YTNlfFRlc3Rpbmc3ODMzfDIwMjUtMDYtMjdUMDY6Mzk6NTNa",
        count: 100,
    };
    return {
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_JSON_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA),
            createJsonCommandPart(singleQuote(JSON.stringify(requestExample, null, 4))),
            createPlainTextCommandPart(baseUrl),
        ]),
        format: "application/json",
        introduction: t("Support.apiGuide.endpoint.fetchAllWorkflowsPost.introduction"),
        linkId: "fetch-workflow-post",
        method: "POST",
        requestExample: requestExample,
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        title: t("Support.apiGuide.endpoint.fetchAllWorkflowsPost.title"),
        url: baseUrl,
        parameterSection: createParameterSection(
            t,
            [],
            [
                {
                    name: "tenant_uuid",
                    required: false,
                    description: t("Support.apiGuide.endpoint.fetchAllWorkflowsPost.parameterDescription.tenant_uuid"),
                },
                {
                    name: "search",
                    required: false,
                    description: t("Support.apiGuide.endpoint.fetchAllWorkflowsPost.parameterDescription.search"),
                },
                {
                    name: "cursor",
                    required: false,
                    description: t("Support.apiGuide.endpoint.fetchAllWorkflowsPost.parameterDescription.cursor"),
                },
                {
                    name: "product",
                    required: false,
                    description: t("Support.apiGuide.endpoint.fetchAllWorkflowsPost.parameterDescription.product"),
                },
                {
                    name: "version",
                    required: false,
                    description: t("Support.apiGuide.endpoint.fetchAllWorkflowsPost.parameterDescription.version"),
                },
                {
                    name: "from",
                    required: false,
                    description: t("Support.apiGuide.endpoint.fetchAllWorkflowsPost.parameterDescription.from"),
                },
                {
                    name: "to",
                    required: false,
                    description: t("Support.apiGuide.endpoint.fetchAllWorkflowsPost.parameterDescription.to"),
                },
            ]
        ),
    };
}

function createLaunchWorkflowPostEndpoint(t: TFunction, url: string): Endpoint {
    const requestExample = {
        parameters: {
            int_variable: 1,
            decimal_variable: 1.5,
            string_variable: "my string",
            boolean_variable: true,
            int_list: [1, 2],
            decimal_list: [1.5, 2.5],
            boolean_list: [true, false],
            string_list: ["a string", "another string"],
            object_list: [
                {
                    int_field: 1,
                    decimal_field: 1.5,
                    boolean_field: true,
                    string_field: "value",
                    list_field: [1, 2],
                    object_field: { field: "value" },
                },
            ],
        },
    };
    const responseExample = {
        context_id: "3d6eddee-1096-437a-a3da-8a6e81c161e5",
    };
    return {
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_JSON_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA),
            createJsonCommandPart(singleQuote(JSON.stringify(requestExample, null, 4))),
            createPlainTextCommandPart(url),
        ]),
        format: "application/json",
        introduction: t("Support.apiGuide.endpoint.launchWorkflowPost.introduction"),
        linkId: "launch-workflow-post",
        method: "POST",
        requestExample: requestExample,
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true, false),
        title: t("Support.apiGuide.endpoint.launchWorkflowPost.title"),
        url: url,
        parameterSection: createParameterSection(
            t,
            [
                {
                    name: "uuid",
                    required: true,
                    description: t("Support.apiGuide.endpoint.launchWorkflowPost.parameterDescription.uuid"),
                },
            ],
            [
                {
                    name: "parameters",
                    required: false,
                    description: t("Support.apiGuide.endpoint.launchWorkflowPost.parameterDescription.parameters"),
                },
            ]
        ),
    };
}

function createFetchWorkflowExecutionStateEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const responseExample = {
        state: "SUCCEEDED",
        started_date: "2026-02-20T12:45:54Z",
        completed_date: "2026-02-20T12:45:54Z",
        return_value: 67,
    };
    return {
        linkId: "fetch-workflow-execution-state",
        title: t("Support.apiGuide.endpoint.fetchWorkflowExecutionState.title"),
        introduction: t("Support.apiGuide.endpoint.fetchWorkflowExecutionState.introduction"),
        url: baseUrl,
        method: "GET",
        format: "application/json",
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true, false),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(
                baseUrl
                    .replace("{uuid}", "490e7ab9-2d39-4475-8843-f648799ed2fb")
                    .replace("{contextId}", "3d6eddee-1096-437a-a3da-8a6e81c161e5")
            ),
        ]),
        parameterSection: createParameterSection(t, [
            {
                name: "uuid",
                required: true,
                description: t("Support.apiGuide.endpoint.fetchWorkflowExecutionState.parameterDescription.uuid"),
            },
            {
                name: "contextId",
                required: true,
                description: t("Support.apiGuide.endpoint.fetchWorkflowExecutionState.parameterDescription.contextId"),
            },
        ]),
    };
}

function createRoleCreationPublicApiEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const requestExample = {
        name: "Reports only role",
        description:
            "User is only allowed to view, export, create, delete reports, and report templates, as well as set default report templates",
        authorities: [
            "AUTH_REPORT_DELETE",
            "AUTH_REPORT_EDIT",
            "AUTH_REPORT_EXPORT_ALL",
            "AUTH_REPORT_IMPORT",
            "AUTH_REPORT_TEMPLATE_CREATE",
            "AUTH_REPORT_TEMPLATE_DELETE",
            "AUTH_REPORT_TEMPLATE_EDIT",
            "AUTH_REPORT_TEMPLATE_VIEW",
            "AUTH_REPORT_TEMPLATE_DEFAULT",
            "AUTH_REPORT_VIEW",
            "AUTH_CUSTOM_REPORT_VIEW_CREATE_OWN",
            "AUTH_CUSTOM_REPORT_VIEW_CREATE_SHARED",
            "AUTH_CUSTOM_REPORT_VIEW_DELETE_OWN",
            "AUTH_CUSTOM_REPORT_VIEW_DELETE_SHARED",
            "AUTH_CUSTOM_REPORT_VIEW_EDIT_OWN",
            "AUTH_CUSTOM_REPORT_VIEW_EDIT_SHARED",
            "AUTH_CUSTOM_REPORT_VIEW_VIEW",
        ],
        targets: {
            authorities: {
                AUTH_REPORT_VIEW: { targets: [{ kind: "relative", value: "GROUP" }] },
            },
        },
        tenant_uuid: "0ca65a20-527c-49b5-836e-efc0f04cb920",
    };
    const responseExample = {
        uuid: "28f372a8-58e9-4468-adec-2623707d289c",
    };

    const requiredFields = ["name", "authorities"];
    const parameters = ["name", "description", "authorities", "tenant_uuid", "targets"].map((each) => ({
        name: each,
        required: requiredFields.includes(each),
        description: t("Support.apiGuide.endpoint.createRolePost.parameterDescription." + each),
    }));

    return {
        linkId: "create-role-post",
        title: t("Support.apiGuide.endpoint.createRolePost.title"),
        introduction: t("Support.apiGuide.endpoint.createRolePost.introduction"),
        url: baseUrl,
        method: "POST",
        format: "application/json",
        requestExample: requestExample,
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_JSON_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA),
            createJsonCommandPart(singleQuote(JSON.stringify(requestExample, null, 4))),
            createPlainTextCommandPart(baseUrl),
        ]),
        parameterSection: createParameterSection(
            t,
            [
                {
                    name: "tenant_uuid",
                    required: false,
                    description: t("Support.apiGuide.endpoint.createRolePost.tenantUuidParameter"),
                },
            ],
            [...parameters]
        ),
    };
}

function createFetchRolePublicApiEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const responseExample = {
        roles: [
            {
                name: "Auditor",
                description: "",
                created_date: "2024-06-25T14:51:12Z",
                edited_date: "2024-06-25T14:51:12Z",
                creator: "Blancco admin",
                default_role: false,
                default_migration_role: false,
                authorities: [
                    "AUTH_LICENSE_VIEW_OWN",
                    "AUTH_CUSTOM_REPORT_VIEW_CREATE_OWN",
                    "AUTH_CUSTOM_REPORT_VIEW_VIEW",
                    "AUTH_ROLE_VIEW",
                    "AUTH_LICENSE_HISTORY_VIEW_OWN",
                    "AUTH_REPORT_VIEW",
                    "AUTH_API_KEY_VIEW_OWN",
                    "AUTH_LICENSE_HISTORY_VIEW_ALL",
                    "AUTH_CUSTOM_REPORT_VIEW_EDIT_OWN",
                    "AUTH_REPORT_TEMPLATE_VIEW",
                    "AUTH_CUSTOM_REPORT_VIEW_DELETE_OWN",
                    "AUTH_WORKFLOW_VIEW",
                    "AUTH_API_KEY_VIEW_ALL",
                    "AUTH_KEY_VALUE_VIEW",
                ],
                role_uuid: "260a5435-559a-49a8-908e-878bf8062b0",
                targets: null,
                total_user_count: 3,
            },
            {
                name: "Basic user",
                description: "",
                created_date: "2024-06-25T14:51:12Z",
                edited_date: "2024-06-25T14:51:12Z",
                creator: "Blancco admin",
                default_role: true,
                default_migration_role: false,
                authorities: [
                    "AUTH_LICENSE_VIEW_OWN",
                    "AUTH_LICENSE_CONSUME",
                    "AUTH_CUSTOM_REPORT_VIEW_CREATE_OWN",
                    "AUTH_WORKFLOW_EDIT",
                    "AUTH_WORKFLOW_CREATE",
                    "AUTH_REPORT_VIEW",
                    "AUTH_WORKFLOW_VIEW",
                    "AUTH_API_KEY_VIEW_ALL",
                    "AUTH_REPORT_TEMPLATE_DELETE",
                    "AUTH_API_KEY_DELETE_OWN",
                    "AUTH_REPORT_TEMPLATE_EDIT",
                    "AUTH_CUSTOM_REPORT_VIEW_VIEW",
                    "AUTH_ROLE_VIEW",
                    "AUTH_REPORT_TEMPLATE_DEFAULT",
                    "AUTH_LICENSE_HISTORY_VIEW_ALL",
                    "AUTH_API_KEY_CREATE_OWN",
                    "AUTH_CUSTOM_REPORT_VIEW_EDIT_OWN",
                    "AUTH_API_KEY_EDIT_OWN",
                    "AUTH_KEY_VALUE_DELETE",
                    "AUTH_WORKFLOW_DELETE",
                    "AUTH_REPORT_EXPORT_ALL",
                    "AUTH_REPORT_IMPORT",
                    "AUTH_KEY_VALUE_EDIT",
                    "AUTH_KEY_VALUE_CREATE",
                    "AUTH_LICENSE_HISTORY_VIEW_OWN",
                    "AUTH_EMS_VIEW",
                    "AUTH_REPORT_TEMPLATE_VIEW",
                    "AUTH_WORKFLOW_DEFAULT",
                    "AUTH_KEY_VALUE_VIEW",
                    "AUTH_API_KEY_VIEW_OWN",
                    "AUTH_CUSTOM_REPORT_VIEW_DELETE_OWN",
                    "AUTH_CUSTOM_REPORT_VIEW_CREATE_SHARED",
                    "AUTH_REPORT_TEMPLATE_CREATE",
                ],
                role_uuid: "a0d2394f-c78d-4238-b19f-b98837556cc0",
                targets: null,
                total_user_count: 42,
            },
            {
                name: "Manager",
                description: "",
                created_date: "2024-06-25T14:51:12Z",
                edited_date: "2024-06-25T14:51:12Z",
                creator: "Blancco admin",
                default_role: false,
                default_migration_role: true,
                authorities: [
                    "AUTH_TENANT_SETTINGS",
                    "AUTH_USER_GROUP_VIEW",
                    "AUTH_WORKFLOW_VIEW",
                    "AUTH_API_KEY_VIEW_OWN",
                    "AUTH_WORKFLOW_CREATE",
                    "AUTH_ROLE_CREATE",
                    "AUTH_LICENSE_VIEW_OWN",
                    "AUTH_LICENSE_VIEW",
                    "AUTH_CUSTOM_REPORT_VIEW_CREATE_SHARED",
                    "AUTH_LICENSE_HISTORY_VIEW_OWN",
                    "AUTH_REPORT_TEMPLATE_EDIT",
                    "AUTH_SSO_CONFIGURE",
                    "AUTH_USER_GROUP_DELETE",
                    "AUTH_USER_GROUP_CREATE",
                    "AUTH_CUSTOM_REPORT_VIEW_EDIT_SHARED",
                    "AUTH_CUSTOM_REPORT_VIEW_DELETE_SHARED",
                    "AUTH_CUSTOM_REPORT_VIEW_DELETE_OWN",
                    "AUTH_REPORT_TEMPLATE_DELETE",
                    "AUTH_WORKFLOW_TEMPLATE_VIEW",
                    "AUTH_LICENSE_CONSUME",
                    "AUTH_REPORT_EXPORT_ALL",
                    "AUTH_TENANT_DELETE",
                    "AUTH_CUSTOM_REPORT_VIEW_EDIT_OWN",
                    "AUTH_MC_UNREGISTER",
                    "AUTH_CUSTOM_REPORT_VIEW_VIEW",
                    "AUTH_KEY_VALUE_VIEW",
                    "AUTH_WORKFLOW_DELETE",
                    "AUTH_REPORT_DELETE",
                    "AUTH_KEY_VALUE_EDIT",
                    "AUTH_REPORT_EDIT",
                    "AUTH_USER_CREATE",
                    "AUTH_KEY_VALUE_CREATE",
                    "AUTH_REPORT_TEMPLATE_CREATE",
                    "AUTH_TENANT_EDIT",
                    "AUTH_WORKFLOW_ENABLE",
                    "AUTH_ROLE_DELETE",
                    "AUTH_USER_EDIT",
                    "AUTH_EMS_CREATE_ENTITLEMENT",
                    "AUTH_EMS_VIEW",
                    "AUTH_TENANT_VIEW",
                    "AUTH_ROLE_VIEW",
                    "AUTH_REPORT_VIEW",
                    "AUTH_TENANT_CREATE",
                    "AUTH_ROLE_DEFAULT",
                    "AUTH_API_KEY_VIEW_ALL",
                    "AUTH_LICENSE_POOL_EDIT",
                    "AUTH_USER_VIEW",
                    "AUTH_API_KEY_CREATE_OWN",
                    "AUTH_MC_VIEW",
                    "AUTH_MC_CREATE",
                    "AUTH_KEY_VALUE_DELETE",
                    "AUTH_TENANT_ACCESS",
                    "AUTH_LICENSE_ASSIGN",
                    "AUTH_LICENSE_POOL_CREATE",
                    "AUTH_REPORT_TEMPLATE_DEFAULT",
                    "AUTH_WORKFLOW_TEMPLATE_SAVE",
                    "AUTH_EMS_SYNCHRONIZE",
                    "AUTH_API_KEY_EDIT_OWN",
                    "AUTH_LICENSE_POOL_DELETE",
                    "AUTH_REPORT_IMPORT",
                    "AUTH_USER_DELETE",
                    "AUTH_API_KEY_DELETE_OWN",
                    "AUTH_LICENSE_HISTORY_VIEW_ALL",
                    "AUTH_REPORT_TEMPLATE_VIEW",
                    "AUTH_LICENSE_POOL_VIEW",
                    "AUTH_USER_GROUP_EDIT",
                    "AUTH_WORKFLOW_DEFAULT",
                    "AUTH_MC_EDIT",
                    "AUTH_LICENSE_ALERT",
                    "AUTH_WORKFLOW_EDIT",
                    "AUTH_CUSTOM_REPORT_VIEW_CREATE_OWN",
                    "AUTH_ROLE_EDIT",
                    "AUTH_LICENSE_KEY_MANAGEMENT",
                ],
                role_uuid: "031d8b41-2c4f-49a3-8d07-6c9991b36762",
                targets: null,
                total_user_count: 0,
            },
        ],
        cursor: null,
    };
    return {
        linkId: "fetch-roles",
        title: t("Support.apiGuide.endpoint.fetchRole.title"),
        introduction: t("Support.apiGuide.endpoint.fetchRole.introduction"),
        url: baseUrl,
        method: "GET",
        format: t("application/json"),
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(`${baseUrl}/`),
        ]),
        parameterSection: createParameterSection(t, [
            {
                name: "tenant_uuid",
                required: false,
                description: t("Support.apiGuide.endpoint.fetchRole.parameterDescription.tenant_uuid"),
            },
            {
                name: "cursor",
                required: false,
                description: t("Support.apiGuide.endpoint.fetchRole.parameterDescription.cursor"),
            },
        ]),
    };
}

function createFetchRoleEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const responseExample = {
        name: "Auditor",
        description: "",
        created_date: "2024-06-25T14:51:12Z",
        edited_date: "2024-06-25T14:51:12Z",
        creator: "john.doe@example.com",
        default_role: false,
        default_migration_role: false,
        authorities: ["AUTH_REPORT_VIEW", "AUTH_ROLE_VIEW"],
        role_uuid: "260a5435-559a-49a8-908e-878bf8062b0a",
        targets: null,
        total_user_count: 3,
    };
    return {
        linkId: "fetch-role",
        title: t("Support.apiGuide.endpoint.fetchSingleRole.title"),
        introduction: t("Support.apiGuide.endpoint.fetchSingleRole.introduction"),
        url: baseUrl,
        method: "GET",
        format: "application/json",
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(baseUrl.replace("{role_uuid}", "260a5435-559a-49a8-908e-878bf8062b0a")),
        ]),
        parameterSection: createParameterSection(t, [
            {
                name: "role_uuid",
                required: true,
                description: t("Support.apiGuide.endpoint.fetchSingleRole.parameterDescription.role_uuid"),
            },
            {
                name: "tenant_uuid",
                required: false,
                description: t("Support.apiGuide.endpoint.fetchSingleRole.parameterDescription.tenant_uuid"),
            },
        ]),
    };
}

function createEditRoleEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const requestExample = {
        name: "Updated role name",
        description: "Updated description",
        authorities: ["AUTH_REPORT_VIEW", "AUTH_ROLE_VIEW", "AUTH_WORKFLOW_VIEW"],
        targets: {
            authorities: {
                AUTH_REPORT_VIEW: { targets: [{ kind: "relative", value: "GROUP" }] },
            },
        },
    };
    return {
        linkId: "edit-role-post",
        title: t("Support.apiGuide.endpoint.editRolePost.title"),
        introduction: t("Support.apiGuide.endpoint.editRolePost.introduction"),
        url: baseUrl,
        method: "POST",
        format: "application/json",
        requestExample: requestExample,
        responseExample: <></>,
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_JSON_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA),
            createJsonCommandPart(singleQuote(JSON.stringify(requestExample, null, 4))),
            createPlainTextCommandPart(baseUrl.replace("{role_uuid}", "260a5435-559a-49a8-908e-878bf8062b0a")),
        ]),
        parameterSection: createParameterSection(
            t,
            [
                {
                    name: "role_uuid",
                    required: true,
                    description: t("Support.apiGuide.endpoint.editRolePost.parameterDescription.role_uuid"),
                },
                {
                    name: "tenant_uuid",
                    required: false,
                    description: t("Support.apiGuide.endpoint.editRolePost.parameterDescription.tenant_uuid"),
                },
            ],
            [
                {
                    name: "tenant_uuid",
                    required: false,
                    description: t("Support.apiGuide.endpoint.editRolePost.parameterDescription.tenant_uuid_body"),
                },
                {
                    name: "name",
                    required: true,
                    description: t("Support.apiGuide.endpoint.editRolePost.parameterDescription.name"),
                },
                {
                    name: "description",
                    required: false,
                    description: t("Support.apiGuide.endpoint.editRolePost.parameterDescription.description"),
                },
                {
                    name: "authorities",
                    required: true,
                    description: t("Support.apiGuide.endpoint.editRolePost.parameterDescription.authorities"),
                },
                {
                    name: "targets",
                    required: false,
                    description: t("Support.apiGuide.endpoint.editRolePost.parameterDescription.targets"),
                },
            ]
        ),
    };
}

function createDeleteRoleEndpoint(t: TFunction, baseUrl: string): Endpoint {
    return {
        linkId: "delete-role",
        title: t("Support.apiGuide.endpoint.deleteRole.title"),
        introduction: t("Support.apiGuide.endpoint.deleteRole.introduction"),
        url: baseUrl,
        method: "DELETE",
        format: t("Common.na"),
        responseExample: <></>,
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XDELETE),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(baseUrl.replace("{role_uuid}", "260a5435-559a-49a8-908e-878bf8062b0a")),
        ]),
        parameterSection: createParameterSection(t, [
            {
                name: "role_uuid",
                required: true,
                description: t("Support.apiGuide.endpoint.deleteRole.parameterDescription.role_uuid"),
            },
            {
                name: "tenant_uuid",
                required: false,
                description: t("Support.apiGuide.endpoint.deleteRole.parameterDescription.tenant_uuid"),
            },
        ]),
    };
}

function createCreateUserGroupEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const requestExample = {
        name: "Auditors",
        role_uuid: "260a5435-559a-49a8-908e-878bf8062b0a",
        users: ["28f372a8-58e9-4468-adec-2623707d289c"],
    };
    const responseExample = {
        group_uuid: "a1b2c3d4-0000-0000-0000-000000000001",
        name: "Auditors",
        role_uuid: "260a5435-559a-49a8-908e-878bf8062b0a",
        role_name: "Auditor",
        total_user_count: 0,
        created: "2024-06-25T14:51:12Z",
        edited: "2024-06-25T14:51:12Z",
    };
    return {
        linkId: "create-user-group-post",
        title: t("Support.apiGuide.endpoint.createUserGroup.title"),
        introduction: t("Support.apiGuide.endpoint.createUserGroup.introduction"),
        url: baseUrl,
        method: "POST",
        format: "application/json",
        requestExample: requestExample,
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_JSON_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA),
            createJsonCommandPart(singleQuote(JSON.stringify(requestExample, null, 4))),
            createPlainTextCommandPart(baseUrl),
        ]),
        parameterSection: createParameterSection(
            t,
            [],
            [
                {
                    name: "name",
                    required: true,
                    description: t("Support.apiGuide.endpoint.createUserGroup.parameterDescription.name"),
                },
                {
                    name: "role_uuid",
                    required: false,
                    description: t("Support.apiGuide.endpoint.createUserGroup.parameterDescription.role_uuid"),
                },
                {
                    name: "users",
                    required: false,
                    description: t("Support.apiGuide.endpoint.createUserGroup.parameterDescription.users"),
                },
                {
                    name: "tenant_uuid",
                    required: false,
                    description: t("Support.apiGuide.endpoint.createUserGroup.parameterDescription.tenant_uuid"),
                },
            ]
        ),
    };
}

function createEditUserGroupEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const requestExample = {
        name: "Senior Auditors",
        role_uuid: "260a5435-559a-49a8-908e-878bf8062b0a",
        _add_users: ["28f372a8-58e9-4468-adec-2623707d289c"],
        _remove_users: [],
    };
    return {
        linkId: "edit-user-group-post",
        title: t("Support.apiGuide.endpoint.editUserGroup.title"),
        introduction: t("Support.apiGuide.endpoint.editUserGroup.introduction"),
        url: baseUrl,
        method: "POST",
        format: "application/json",
        requestExample: requestExample,
        responseExample: <></>,
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_JSON_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA),
            createJsonCommandPart(singleQuote(JSON.stringify(requestExample, null, 4))),
            createPlainTextCommandPart(baseUrl.replace("{group_uuid}", "a1b2c3d4-0000-0000-0000-000000000001")),
        ]),
        parameterSection: createParameterSection(
            t,
            [
                {
                    name: "group_uuid",
                    required: true,
                    description: t("Support.apiGuide.endpoint.editUserGroup.parameterDescription.group_uuid"),
                },
            ],
            [
                {
                    name: "name",
                    required: false,
                    description: t("Support.apiGuide.endpoint.editUserGroup.parameterDescription.name"),
                },
                {
                    name: "role_uuid",
                    required: false,
                    description: t("Support.apiGuide.endpoint.editUserGroup.parameterDescription.role_uuid"),
                },
                {
                    name: "users",
                    required: false,
                    description: t("Support.apiGuide.endpoint.editUserGroup.parameterDescription.users"),
                },
                {
                    name: "_add_users",
                    required: false,
                    description: t("Support.apiGuide.endpoint.editUserGroup.parameterDescription._add_users"),
                },
                {
                    name: "_remove_users",
                    required: false,
                    description: t("Support.apiGuide.endpoint.editUserGroup.parameterDescription._remove_users"),
                },
            ]
        ),
    };
}

function createFetchUserGroupsEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const responseExample = {
        user_groups: [
            {
                group_uuid: "a1b2c3d4-0000-0000-0000-000000000001",
                name: "Auditors",
                role_uuid: "260a5435-559a-49a8-908e-878bf8062b0a",
                role_name: "Auditor",
                total_user_count: 5,
                created: "2024-06-25T14:51:12Z",
                edited: "2024-06-25T14:51:12Z",
            },
        ],
    };
    return {
        linkId: "fetch-user-groups",
        title: t("Support.apiGuide.endpoint.fetchUserGroups.title"),
        introduction: t("Support.apiGuide.endpoint.fetchUserGroups.introduction"),
        url: baseUrl,
        method: "GET",
        format: "application/json",
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(baseUrl),
        ]),
        parameterSection: createParameterSection(t, [
            {
                name: "X-Blancco-Cursor",
                required: false,
                description: t("Support.apiGuide.endpoint.fetchUserGroups.parameterDescription.cursor"),
            },
        ]),
    };
}

function createFetchUserGroupEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const responseExample = {
        group_uuid: "a1b2c3d4-0000-0000-0000-000000000001",
        name: "Auditors",
        role_uuid: "260a5435-559a-49a8-908e-878bf8062b0a",
        role_name: "Auditor",
        total_user_count: 5,
        created: "2024-06-25T14:51:12Z",
        edited: "2024-06-25T14:51:12Z",
    };
    return {
        linkId: "fetch-user-group",
        title: t("Support.apiGuide.endpoint.fetchUserGroup.title"),
        introduction: t("Support.apiGuide.endpoint.fetchUserGroup.introduction"),
        url: baseUrl,
        method: "GET",
        format: "application/json",
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(baseUrl.replace("{group_uuid}", "a1b2c3d4-0000-0000-0000-000000000001")),
        ]),
        parameterSection: createParameterSection(t, [
            {
                name: "group_uuid",
                required: true,
                description: t("Support.apiGuide.endpoint.fetchUserGroup.parameterDescription.group_uuid"),
            },
        ]),
    };
}

function createDeleteUserGroupEndpoint(t: TFunction, baseUrl: string): Endpoint {
    return {
        linkId: "delete-user-group",
        title: t("Support.apiGuide.endpoint.deleteUserGroup.title"),
        introduction: t("Support.apiGuide.endpoint.deleteUserGroup.introduction"),
        url: baseUrl,
        method: "DELETE",
        format: t("Common.na"),
        responseExample: <></>,
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XDELETE),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(baseUrl.replace("{group_uuid}", "a1b2c3d4-0000-0000-0000-000000000001")),
        ]),
        parameterSection: createParameterSection(t, [
            {
                name: "group_uuid",
                required: true,
                description: t("Support.apiGuide.endpoint.deleteUserGroup.parameterDescription.group_uuid"),
            },
        ]),
    };
}

function createUserCreationPublicApiEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const requestExample = {
        name: "Jane Doe",
        email: "jane.doe@example.com",
        password: "GreatPassword1!",
        create_api_key: true,
        tenant_uuid: "0ca65a20-527c-49b5-836e-efc0f04cb920",
        role_uuid: "038e359e-a93c-42d7-9578-2d7331a4026d",
        api_key: {
            expiration: "2027-06-25",
            scope: "ALL",
        },
    };
    const responseExample = {
        user: {
            uuid: "28f372a8-58e9-4468-adec-2623707d289c",
            api_key: "703ae0b0-a3db-4a94-98e1-2b3e0cc5b0a2",
        },
    };
    const parameters = [
        "name",
        "email",
        "password",
        "create_api_key",
        "tenant_uuid",
        "role_uuid",
        "api_key.expiration",
        "api_key.scope",
    ].map((each) => ({
        name: each,
        required: each == "email",
        description: t("Support.apiGuide.endpoint.createUsersPost.parameterDescription." + each),
    }));

    return {
        linkId: "create-users-post",
        title: t("Support.apiGuide.endpoint.createUsersPost.title"),
        introduction: t("Support.apiGuide.endpoint.createUsersPost.introduction"),
        url: baseUrl,
        method: "POST",
        format: "application/json",
        requestExample: requestExample,
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_JSON_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA),
            createJsonCommandPart(singleQuote(JSON.stringify(requestExample, null, 4))),
            createPlainTextCommandPart(baseUrl),
        ]),
        parameterSection: createParameterSection(t, [], [...parameters]),
        description: t("Support.apiGuide.paths.userTypeDescription"),
        mainTitle: t("Support.apiGuide.paths.userTypeTitle"),
    };
}

function createUserEditPublicApiEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const requestExample = {
        name: "Jane Doe",
        role: "038e359e-a93c-42d7-9578-2d7331a4026d",
        enabled: true,
    };
    const parameters = ["name", "role", "enabled"].map((each) => ({
        name: each,
        required: false,
        description: t("Support.apiGuide.endpoint.editUsersPost.parameterDescription." + each),
    }));

    return {
        linkId: "edit-users-post",
        title: t("Support.apiGuide.endpoint.editUsersPost.title"),
        introduction: t("Support.apiGuide.endpoint.editUsersPost.introduction"),
        url: baseUrl,
        method: "POST",
        format: "application/json",
        requestExample: requestExample,
        responseExample: <></>,
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_JSON_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA),
            createJsonCommandPart(singleQuote(JSON.stringify(requestExample, null, 4))),
            createPlainTextCommandPart(baseUrl),
        ]),
        parameterSection: createParameterSection(t, [], [...parameters]),
    };
}

function createFetchUsersEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const responseExample = {
        users: [
            {
                uuid: "28f372a8-58e9-4468-adec-2623707d289c",
                username: "jane.doe@example.com",
                email: "jane.doe@example.com",
                name: "Jane Doe",
                enabled: true,
                expiration_date: null,
                role_uuid: "038e359e-a93c-42d7-9578-2d7331a4026d",
                groups: ["a1b2c3d4-0000-0000-0000-000000000001"],
                last_login: "2024-06-25T14:51:12Z",
            },
        ],
    };
    return {
        linkId: "fetch-users",
        title: t("Support.apiGuide.endpoint.fetchUsers.title"),
        introduction: t("Support.apiGuide.endpoint.fetchUsers.introduction"),
        url: baseUrl,
        method: "GET",
        format: "application/json",
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(`${baseUrl}?enabled=true&search=jane`),
        ]),
        parameterSection: createParameterSection(t, [
            {
                name: "enabled",
                required: false,
                description: t("Support.apiGuide.endpoint.fetchUsers.parameterDescription.enabled"),
            },
            {
                name: "search",
                required: false,
                description: t("Support.apiGuide.endpoint.fetchUsers.parameterDescription.search"),
            },
            {
                name: "page_size",
                required: false,
                description: t("Support.apiGuide.endpoint.fetchUsers.parameterDescription.pageSize"),
            },
            {
                name: "X-Blancco-Cursor",
                required: false,
                description: t("Support.apiGuide.endpoint.fetchUsers.parameterDescription.cursor"),
            },
        ]),
    };
}

function createFetchUserEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const responseExample = {
        uuid: "28f372a8-58e9-4468-adec-2623707d289c",
        username: "jane.doe@example.com",
        email: "jane.doe@example.com",
        name: "Jane Doe",
        enabled: true,
        expiration_date: null,
        role_uuid: "038e359e-a93c-42d7-9578-2d7331a4026d",
        groups: ["a1b2c3d4-0000-0000-0000-000000000001"],
        last_login: "2024-06-25T14:51:12Z",
    };
    return {
        linkId: "fetch-user",
        title: t("Support.apiGuide.endpoint.fetchUser.title"),
        introduction: t("Support.apiGuide.endpoint.fetchUser.introduction"),
        url: baseUrl,
        method: "GET",
        format: "application/json",
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(baseUrl.replace("{uuid}", "28f372a8-58e9-4468-adec-2623707d289c")),
        ]),
        parameterSection: createParameterSection(t, [
            {
                name: "uuid",
                required: true,
                description: t("Support.apiGuide.endpoint.fetchUser.parameterDescription.uuid"),
            },
        ]),
    };
}

function createDeleteUserEndpoint(t: TFunction, baseUrl: string): Endpoint {
    return {
        linkId: "delete-user",
        title: t("Support.apiGuide.endpoint.deleteUser.title"),
        introduction: t("Support.apiGuide.endpoint.deleteUser.introduction"),
        url: baseUrl,
        method: "DELETE",
        format: t("Common.na"),
        responseExample: <></>,
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XDELETE),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(baseUrl.replace("{uuid}", "28f372a8-58e9-4468-adec-2623707d289c")),
        ]),
        parameterSection: createParameterSection(t, [
            {
                name: "uuid",
                required: true,
                description: t("Support.apiGuide.endpoint.deleteUser.parameterDescription.uuid"),
            },
        ]),
    };
}

function createTenantCreationPublicApiEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const requestExample = {
        name: "Jane Doe",
        contact_email: "jane.doe@example.com",
        contact_name: "Doe Jane",
        ...(isUserParentInternal() && { salesforce_id: "12345" }),
        type: "CUSTOMER",
        contract_type: "PRODUCTION",
        region: "eu-west-1",
        tier: "BASIC",
        licensing_model: "OLD_MODEL",
        country_code: "DZ",
        notes: "",
        ...(isUserParentInternal() && {
            ems: {
                ems_id: null,
                hl_entitlements: false,
                sl_entitlements: false,
                available_sl_activations: 0,
                used_sl_activations: 0,
                last_synced: "",
            },
        }),
        license_delivery: {
            type: "NEW_DEAL",
            ...(isUserParentInternal() && { case_number: "1234", opportunity_id: "1234" }),
            notes: "",
            licenses: [
                {
                    license_id: "70",
                    amount: 2,
                    expiration_date: "2091-01-01T21:59:59Z",
                },
            ],
        },
        expiration_date: "",
        drawback_prevention: false,
        ...(isUserParentInternal() && {
            salesforce_contract_id: "TEST123456789abcde",
            salesforce_contact_email: "",
            salesforce_contract_state: "",
            salesforce_contract_end_date: "",
            salesforce_lead_id: "",
        }),
        automatic_deletion: false,
    };
    const responseExample = {
        tenant: {
            name: "John Doe",
            ...(isUserParentInternal() && { salesforce_id: "12345" }),
            tenant_uuid: "cd5030a7-95ec-45b2-9b2f-bae71f8f41da",
            warnings: [
                "USER_CREATION_FAILED",
                "LICENSE_DELIVERY_CREATION_FAILED",
                ...(isUserParentInternal() ? ["EMS_CONFIGURATION_FAILED"] : []),
            ],
        },
    };
    const queryParameters: RequestParameter[] = [
        {
            name: "tenant_uuid",
            required: false,
            description: t("Support.apiGuide.endpoint.createTenantPost.description"),
        },
    ];

    const requiredFields = ["name", "type", "region", "tier", "licensing_model", "country_code"];
    if (isUserParentInternal()) {
        requiredFields.push("drawback_prevention");
        requiredFields.push("salesforce_id");
        requiredFields.push("contact_email");
        requiredFields.push("contact_name");
    }

    const bodyParameters: RequestParameter[] = [
        "name",
        "contact_email",
        "contact_name",
        ...(isUserParentInternal() ? ["salesforce_id", "ems"] : []),
        "type",
        "contract_type",
        "region",
        "tier",
        "licensing_model",
        "country_code",
        "notes",
        "license_delivery",
        "expiration_date",
        "drawback_prevention",
        ...(isUserParentInternal()
            ? [
                  "salesforce_contract_id",
                  "salesforce_contact_email",
                  "salesforce_contract_state",
                  "salesforce_contract_end_date",
                  "salesforce_lead_id",
              ]
            : []),
        "automatic_deletion",
    ].map((each) => ({
        name: each,
        required: requiredFields.includes(each),
        internal: INTERNAL_FIELDS.has(each),
        description: t("Support.apiGuide.endpoint.createTenantPost.parameterDescription." + each),
    }));

    return {
        linkId: "create-tenants-post",
        title: t("Support.apiGuide.endpoint.createTenantPost.title"),
        introduction: t("Support.apiGuide.endpoint.createTenantPost.introduction"),
        url: baseUrl,
        method: "POST",
        format: "application/json",
        requestExample: requestExample,
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_JSON_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA),
            createJsonCommandPart(singleQuote(JSON.stringify(requestExample, null, 4))),
            createPlainTextCommandPart(baseUrl),
        ]),
        parameterSection: createParameterSection(t, [...queryParameters], [...bodyParameters]),
    };
}

function createTenantEditPublicApiEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const requestExample = {
        name: "Jane Doe",
        contract_type: "PRODUCTION",
        contact_email: "jane.doe@example.com",
        contact_name: "Doe Jane",
        ...(isUserParentInternal() && { salesforce_id: "12345" }),
        type: "CUSTOMER",
        tier: "BASIC",
        licensing_model: "OLD_MODEL",
        country_code: "DZ",
        notes: "",
        ...(isUserParentInternal() && {
            ems: {
                ems_id: null,
                hl_entitlements: false,
                sl_entitlements: false,
                available_sl_activations: 0,
            },
        }),
        license_delivery: {
            type: "NEW_DEAL",
            ...(isUserParentInternal() && { case_number: "1234", opportunity_id: "1234" }),
            notes: "",
            assignment_mode: "SET",
            licenses: [
                {
                    license_id: "70",
                    amount: 2,
                    expiration_date: "2091-01-01T21:59:59Z",
                    assignment_mode: "SET",
                },
            ],
        },
        expiration_date: "",
        drawback_prevention: false,
        ...(isUserParentInternal() && {
            salesforce_lead_id: "TEST123456789abcde",
            salesforce_contract_id: "TEST123456789abcde",
            salesforce_contact_email: "",
            salesforce_contract_state: "",
            salesforce_contract_end_date: "",
        }),
        automatic_deletion: false,
    };
    const responseExample = {
        warnings: [
            "USER_CREATION_FAILED",
            "LICENSE_DELIVERY_CREATION_FAILED",
            ...(isUserParentInternal() ? ["EMS_CONFIGURATION_FAILED"] : []),
        ],
    };
    const pathParameters: RequestParameter[] = [
        {
            name: "uuid",
            required: true,
            description: t("Support.apiGuide.endpoint.updateTenantPost.uuidParameter"),
        },
    ];

    const bodyParameters: RequestParameter[] = [
        "name",
        "contract_type",
        "contact_email",
        "contact_name",
        ...(isUserParentInternal() ? ["salesforce_id", "ems"] : []),
        "type",
        "region",
        "tier",
        "licensing_model",
        "country_code",
        "notes",
        "license_delivery",
        "expiration_date",
        "drawback_prevention",
        ...(isUserParentInternal()
            ? [
                  "salesforce_lead_id",
                  "salesforce_contract_id",
                  "salesforce_contact_email",
                  "salesforce_contract_state",
                  "salesforce_contract_end_date",
              ]
            : []),
        "automatic_deletion",
    ].map((each) => ({
        name: each,
        required: false,
        internal: INTERNAL_FIELDS.has(each),
        description: t("Support.apiGuide.endpoint.updateTenantPost.parameterDescription." + each),
    }));

    return {
        linkId: "update-tenants-post",
        title: t("Support.apiGuide.endpoint.updateTenantPost.title"),
        introduction: t("Support.apiGuide.endpoint.updateTenantPost.introduction"),
        url: baseUrl,
        method: "POST",
        format: "application/json",
        requestExample: requestExample,
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_JSON_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA),
            createJsonCommandPart(singleQuote(JSON.stringify(requestExample, null, 4))),
            createPlainTextCommandPart(baseUrl),
        ]),
        parameterSection: createParameterSection(t, [...pathParameters], [...bodyParameters]),
    };
}

function createFetchTenantPublicApiEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const responseExample = {
        tenant: {
            name: "some tenant",
            uuid: "some-uuid",
            total_reports: 12,
            ...(isUserParentInternal() && { salesforce_id: 1234 }),
            licensing_model: "some model",
            status: true,
            tier: "some tier",
            created_timestamp: "2024-03-15T10:14:04Z",
            expiration_date: "",
            type: "tenant type",
            contact: {
                username: "contact@customer.com",
                active: true,
            },
            drawback_prevention: false,
            ...(isUserParentInternal() && {
                ems_configuration: {
                    available_sl_activations: 0,
                    ems_id: "ems-id",
                    hl_entitlements: false,
                    sl_entitlements: false,
                    used_sl_activations: 0,
                },
            }),
        },
    };
    return {
        linkId: "fetch-tenant",
        title: t("Support.apiGuide.endpoint.fetchTenant.title"),
        introduction: t("Support.apiGuide.endpoint.fetchTenant.introduction"),
        url: baseUrl,
        method: "GET",
        format: t("application/json"),
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(`${baseUrl}/4715f169-1229-4315-90b4-c125a031e865`),
        ]),
        parameterSection: createParameterSection(t, [
            {
                name: "uuid",
                required: true,
                description: t("Support.apiGuide.endpoint.fetchTenant.uuidParameter"),
            },
        ]),
    };
}

function createFetchTenantsLicensesPublicApiEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const responseExample = {
        licenses: [
            {
                product_id: "1",
                product: "Drive Eraser - Asset Profiler",
                balance: 123,
                expiration_date: "2090-12-31T23:59:59Z",
            },
        ],
        cursor: null,
    };

    return {
        linkId: "tenants-licenses-get",
        title: t("Support.apiGuide.endpoint.fetchTenantsLicenses.title"),
        introduction: t("Support.apiGuide.endpoint.fetchTenantsLicenses.introduction"),
        url: baseUrl,
        method: "GET",
        format: "application/json, text/csv",
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(`${baseUrl}?tenant_uuid=99793e43-ea44-4295-9edd-6cd95ee75a3e`),
        ]),
        parameterSection: createParameterSection(t, [
            {
                name: "tenant_uuid",
                required: false,
                description: t("Support.apiGuide.endpoint.fetchTenantsLicenses.tenantUuidParameter"),
            },
            {
                name: "export",
                required: false,
                description: t("Support.apiGuide.endpoint.fetchTenantsLicenses.exportParameter"),
            },
        ]),
    };
}
function createLicenseDeliveryPublicApiEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const requestExample = {
        type: "NEW_DEAL",
        ...(isUserParentInternal() && {
            case_number: "1234",
            opportunity_id: "1234",
            rate_uuid: "5ee75a3e-ea44-4295-9edd-6cd999793e43",
        }),
        notes: "testing public api",
        target_date: "2027-12-12T20:56:56Z",
        status: "SCHEDULED",
        assignment_mode: "SET",
        licenses: [
            {
                license_id: "license_id",
                amount: 1,
                expiration_date: "YYYY-mm-ddTHH:MM:SSZ",
                assignment_mode: "SET",
            },
        ],
    };

    const parameters: RequestParameter[] = [
        ...(isUserParentInternal() ? ["opportunity_id", "case_number", "rate_uuid"] : []),
        "notes",
        "licenses",
        "license_id",
        "amount",
        "expiration_date",
        "target_date",
        "status",
        "assignment_mode",
    ].map((each) => ({
        name: each,
        required: !["targetDate", "status", "assignment_mode"].includes(each),
        internal: INTERNAL_FIELDS.has(each),
        description: t("Support.apiGuide.endpoint.createLicenseDeliveryPost.parameterDescription." + each),
    }));
    parameters.push({
        name: "type",
        required: false,
        description: t("Support.apiGuide.endpoint.createLicenseDeliveryPost.parameterDescription.type", {
            allowedDeliveries: !isUserParentInternal()
                ? deliveryTypes.join(", ")
                : [...deliveryTypes, "TRIAL"].join(", "),
        }),
    });

    return {
        linkId: "create-license-delivery-post",
        title: t("Support.apiGuide.endpoint.createLicenseDeliveryPost.title"),
        introduction: t("Support.apiGuide.endpoint.createLicenseDeliveryPost.introduction"),
        url: baseUrl,
        method: "POST",
        format: "application/json",
        requestExample: requestExample,
        responseExample: <></>,
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_JSON_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA),
            createJsonCommandPart(singleQuote(JSON.stringify(requestExample, null, 4))),
            createPlainTextCommandPart(baseUrl),
        ]),
        parameterSection: createParameterSection(
            t,
            [
                {
                    name: "tenant_uuid",
                    required: true,
                    description: t("Support.apiGuide.endpoint.createLicenseDeliveryPost.tenantUuidParameter"),
                },
            ],
            [...parameters]
        ),
        mainTitle: t("Support.apiGuide.paths.licenseTypeTitle"),
        description: t("Support.apiGuide.paths.licenseTypeDescription"),
    };
}

function createFetchTenantsPublicApiEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const responseExample = {
        tenants: [
            {
                name: "some tenant",
                uuid: "some-uuid",
                total_reports_count: 12,
                total_users_count: 10,
                onboarding_state: {
                    BMDE: "VIEWING_REPORT",
                    BDE: "VIEWING_REPORT",
                },
                total_workflows_count: 2,
                ...(isUserParentInternal() && { salesforce_id: 1234 }),
                licensing_model: "some model",
                status: true,
                tier: "some tier",
                created_timestamp: "2024-03-15T10:14:04Z",
                expiration_date: "",
                type: "tenant type",
                parent_uuid: "parent-uuid",
                subtenants_count: 12,
                contact: {
                    username: "contact@customer.com",
                    active: true,
                },
                drawback_prevention: false,
                licenses: [
                    {
                        product_id: "12",
                        product: "product name",
                        assigned: "23",
                        balance: 0,
                        overall_balance: 0,
                        expiration_date: "",
                        consumed: 0,
                        overall_consumption: 0,
                    },
                ],
                bmp_on_premise: {
                    selected_desired_interval: 3600,
                    selected_deadline_interval: 604800,
                    offline_sync: false,
                },
                last_login: "2023-01-01T00:00Z",
                last_logged_in_user: "contact@customer.com",
                total_logins_count: 23,
            },
        ],
        cursor: null,
    };
    const queryParameters: RequestParameter[] = [
        ...(isUserParentInternal()
            ? [
                  {
                      name: "salesforce_id",
                      required: false,
                      internal: true,
                      description: t("Support.apiGuide.endpoint.fetchTenantsApi.parameterDescription.salesforceId"),
                  },
              ]
            : []),
        {
            name: "parent_uuid",
            required: false,
            description: t("Support.apiGuide.endpoint.fetchTenantsApi.parameterDescription.parentUuid"),
        },
        {
            name: "licenses_available",
            required: false,
            description: t("Support.apiGuide.endpoint.fetchTenantsApi.parameterDescription.licensesAvailable"),
        },
        {
            name: "license_usage",
            required: false,
            description: t("Support.apiGuide.endpoint.fetchTenantsApi.parameterDescription.licenseUsage"),
        },
        {
            name: "date",
            required: false,
            description: t("Support.apiGuide.endpoint.fetchTenantsApi.parameterDescription.date"),
        },
        {
            name: "until_date",
            required: false,
            description: t("Support.apiGuide.endpoint.fetchTenantsApi.parameterDescription.untilDate"),
        },
        {
            name: "page_size",
            required: false,
            description: t("Support.apiGuide.endpoint.fetchTenantsApi.parameterDescription.pageSize"),
        },
        {
            name: "recursive",
            required: false,
            description: t("Support.apiGuide.endpoint.fetchTenantsApi.parameterDescription.recursiveFlag"),
        },
        {
            name: "tenant_uuid",
            required: false,
            description: t("Support.apiGuide.endpoint.fetchTenantsApi.parameterDescription.tenantUuid"),
        },
    ];
    return {
        linkId: "fetch-tenants",
        title: t("Support.apiGuide.endpoint.fetchTenantsApi.title"),
        introduction: t("Support.apiGuide.endpoint.fetchTenantsApi.description"),
        url: baseUrl,
        method: "GET",
        format: t("application/json"),
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(`${baseUrl}`),
        ]),
        parameterSection: createParameterSection(t, queryParameters, []),

        mainTitle: t("Support.apiGuide.paths.tenantTypeTitle"),
        description: t("Support.apiGuide.paths.tenantTypeDescription"),
    };
}

function createFetchTenantsEsgDataPublicApiEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const responseExample = {
        sustainability_data: {
            months: [
                {
                    month: "2023-04",
                    content: {
                        laptop: {
                            weight: 0,
                            co2: 0,
                        },
                        desktop: {
                            weight: 0,
                            co2: 0,
                        },
                        server: {
                            weight: 0,
                            co2: 0,
                        },
                        loose_drive: {
                            weight: 0,
                            co2: 0,
                        },
                        smartphone: {
                            weight: 0,
                            co2: 0,
                        },
                        tablet: {
                            weight: 0,
                            co2: 0,
                        },
                    },
                },
            ],
            cursor: null,
        },
    };

    return {
        linkId: "tenants-esg-data-get",
        title: t("Support.apiGuide.endpoint.fetchTenantsEsgData.title"),
        introduction: t("Support.apiGuide.endpoint.fetchTenantsEsgData.introduction"),
        url: baseUrl,
        method: "GET",
        format: "application/json",
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(`${baseUrl}?tenant_uuid=99793e43-ea44-4295-9edd-6cd95ee75a3e`),
        ]),
        parameterSection: createParameterSection(t, [
            {
                name: "tenant_uuid",
                required: false,
                description: t("Support.apiGuide.endpoint.fetchTenantsEsgData.tenantUuidParameter"),
            },
        ]),
    };
}

function createFetchTenantsLicenseEventsPublicApiEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const responseExample = {
        events: [
            {
                product: "1",
                amount_before: 123,
                amount_after: 124,
                expiration_before: "2023-01-01T00:00:00Z",
                expiration_after: "2023-01-01T00:00:00Z",
                event_type: "DELIVERY",
                used_amount: 11,
                product_name: "Drive Eraser - Asset Profiler",
                product_used: "Driver Eraser - AP",
                product_version: "6.8.0",
                extra: "Additional details",
            },
            {
                product: "2",
                amount_before: 3,
                amount_after: 2,
                expiration_before: "2023-01-01T00:00:00Z",
                expiration_after: "2023-01-01T00:00:00Z",
                event_type: "CONSUMPTION",
                used_amount: 23,
                product_name: "Mobile Diagnostics & Erasure - Device Image Validation",
                product_used: "Mobile Diagnostics & Erasure - DIV",
                product_version: "6.1.0",
                extra: "Additional details",
            },
        ],
    };
    const filterParameters = [
        {
            name: "start_date",
            required: false,
            description: t("Support.apiGuide.endpoint.fetchTenantsLicenseEventsData.parameterDescription.startDate"),
        },
        {
            name: "end_date",
            required: false,
            description: t("Support.apiGuide.endpoint.fetchTenantsLicenseEventsData.parameterDescription.endDate"),
        },
    ];
    return {
        linkId: "tenants-license-events-get",
        title: t("Support.apiGuide.endpoint.fetchTenantsLicenseEventsData.title"),
        introduction: t("Support.apiGuide.endpoint.fetchTenantsLicenseEventsData.introduction"),
        url: baseUrl,
        method: "POST",
        format: "application/json",
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(`${baseUrl}?tenant_uuid=99793e43-ea44-4295-9edd-6cd95ee75a3e`),
        ]),
        parameterSection: createParameterSection(
            t,
            [
                {
                    name: "tenant_uuid",
                    required: true,
                    description: t("Support.apiGuide.endpoint.fetchTenantsLicenseEventsData.tenantUuidParameter"),
                },
            ],
            [...filterParameters]
        ),
    };
}

function createTenantNameAvailabilityEndpoint(available: boolean, t: TFunction, baseUrl: string) {
    if (!available) {
        return null;
    }
    const responseExample = {
        available: true,
    };
    return {
        linkId: "tenant-availability-get",
        title: t("Support.apiGuide.endpoint.checkTenantNameAvailability.title"),
        introduction: t("Support.apiGuide.endpoint.checkTenantNameAvailability.introduction"),
        url: baseUrl,
        method: "GET",
        format: "application/json",
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(`${baseUrl}?name=foobar`),
        ]),
        parameterSection: createParameterSection(t, [
            {
                name: "name",
                required: true,
                description: t("Support.apiGuide.endpoint.checkTenantNameAvailability.tenantNameParameter"),
            },
        ]),
        internal: true,
    };
}

function createUsernameAvailabilityEndpoint(available: boolean, t: TFunction, baseUrl: string) {
    if (!available) {
        return null;
    }
    const responseExample = {
        available: true,
    };
    return {
        linkId: "username-availability-get",
        title: t("Support.apiGuide.endpoint.checkUsernameAvailability.title"),
        introduction: t("Support.apiGuide.endpoint.checkUsernameAvailability.introduction"),
        url: baseUrl,
        method: "GET",
        format: "application/json",
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(`${baseUrl}?username=testuser@blancco.com`),
        ]),
        parameterSection: createParameterSection(t, [
            {
                name: "username",
                required: true,
                description: t("Support.apiGuide.endpoint.checkUsernameAvailability.usernameParameter"),
            },
        ]),
        internal: true,
    };
}

function createAboutEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const responseExample = {
        platform: {
            version: "1.0.0",
            environment: "production",
            region: "eu-west-1",
        },
        user: {
            username: "jane.doe@example.com",
            uuid: "28f372a8-58e9-4468-adec-2623707d289c",
        },
        tenant: {
            name: "Acme Corp",
            uuid: "0ca65a20-527c-49b5-836e-efc0f04cb920",
            country: "FIN",
        },
    };
    return {
        linkId: "get-about",
        title: t("Support.apiGuide.endpoint.getAbout.title"),
        introduction: t("Support.apiGuide.endpoint.getAbout.introduction"),
        url: baseUrl,
        method: "GET",
        format: "application/json",
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(baseUrl),
        ]),
        mainTitle: t("Support.apiGuide.paths.aboutTypeTitle"),
        description: t("Support.apiGuide.paths.aboutTypeDescription"),
    };
}

function createAuditLogsExportEndpoint(t: TFunction, baseUrl: string): Endpoint {
    return {
        linkId: "export-audit-logs",
        title: t("Support.apiGuide.endpoint.exportAuditLogs.title"),
        introduction: t("Support.apiGuide.endpoint.exportAuditLogs.introduction"),
        url: baseUrl,
        method: "GET",
        format: "text/csv",
        responseExample: createTextExample(
            t("Support.apiGuide.response"),
            t("Support.apiGuide.endpoint.exportAuditLogs.responseDescription")
        ),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(`${baseUrl}?start_date=2024-01-01T00:00:00Z&end_date=2024-12-31T23:59:59Z`),
        ]),
        parameterSection: createParameterSection(t, [
            {
                name: "username",
                required: false,
                description: t("Support.apiGuide.endpoint.exportAuditLogs.parameterDescription.username_prefix"),
            },
            {
                name: "start_date",
                required: false,
                description: t("Support.apiGuide.endpoint.exportAuditLogs.parameterDescription.start_date"),
            },
            {
                name: "end_date",
                required: false,
                description: t("Support.apiGuide.endpoint.exportAuditLogs.parameterDescription.end_date"),
            },
            {
                name: "cursor",
                required: false,
                description: t("Support.apiGuide.endpoint.exportReportsPost.parameterDescription.cursor"),
            },
        ]),
        mainTitle: t("Support.apiGuide.paths.auditLogTypeTitle"),
        description: t("Support.apiGuide.paths.auditLogTypeDescription"),
    };
}

function createLicenseKeyCreationEndpoint(t: TFunction, baseUrl: string) {
    const requestExample = {
        license_key: "1234-5678-9012-3456",
        identifier: "client-001",
        assign_count: 3,
        start_date: "2025-01-12T00:00:00Z",
        expiration_date: "2025-01-13T00:00:00Z",
        license_id: "82",
        tenant_uuid: "9479831b-fc85-4c79-ac2b-504c54482f28",
    };
    const parameters = Object.keys(requestExample).map((each) => ({
        name: each,
        required: !["tenant_uuid", "identifier"].includes(each),
        description: t("Support.apiGuide.endpoint.createLicenseKey.parameterDescription." + each),
    }));
    return {
        linkId: "license-key-creation-post",
        title: t("Support.apiGuide.endpoint.createLicenseKey.title"),
        introduction: t("Support.apiGuide.endpoint.createLicenseKey.introduction"),
        url: baseUrl,
        method: "POST",
        format: "application/json",
        responseExample: createJsonExample(t("Support.apiGuide.response"), requestExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_JSON_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA),
            createJsonCommandPart(singleQuote(JSON.stringify(requestExample, null, 4))),
            createPlainTextCommandPart(baseUrl),
            createPlainTextCommandPart(""),
            createPlainTextCommandPart("or for bulk creation"),
            createPlainTextCommandPart(""),
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart("--location 'https://api.dev-eu-west-1.blancco.cloud/v1/license-keys'"),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart('-H Content-Type: text/csv/"'),
            createPlainTextCommandPart("--data-binary '@/license_keys.csv'"),
        ]),
        parameterSection: createParameterSection(t, parameters),
    };
}
function createAssetExportPublicApiEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const requestExample = {
        filters: [
            {
                name: "asset_state",
                value: "Active",
                operator: "EQUALS",
                path_type: "KEYWORD",
            },
            {
                name: "asset_type",
                value: "Laptop",
                operator: "EQUALS",
                path_type: "KEYWORD",
            },
        ],
        paths: ["asset_id", "serial_number", "asset_type", "manufacturer", "model", "asset_state"],
        sort_column: "asset_id",
        sort_column_type: "STRING",
        sort_ascending: true,
        search: "Blancco",
        filter_logic: "0 AND 1",
        group_uuid: "3982bf66-514b-472c-beb2-c9772e34d977",
        data_bearing_filter: "ALL",
        start_date: "2024-01-01T00:00:00Z",
        end_date: "2024-12-31T23:59:59Z",
    };

    return {
        linkId: "export-assets-public-post",
        title: t("Support.apiGuide.endpoint.exportAssetsPublicPost.title"),
        introduction: t("Support.apiGuide.endpoint.exportAssetsPublicPost.introduction"),
        url: baseUrl,
        method: "POST",
        format: "text/csv",
        requestExample: requestExample,
        responseExample: createTextExample(
            t("Support.apiGuide.response"),
            t("Support.apiGuide.endpoint.exportAssetsPublicPost.responseDescription")
        ),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_JSON_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA),
            createJsonCommandPart(singleQuote(JSON.stringify(requestExample, null, 4))),
            createPlainTextCommandPart(baseUrl),
        ]),
        parameterSection: createParameterSection(
            t,
            [
                {
                    name: "X-BLANCCO-CURSOR",
                    required: false,
                    description: t("Support.apiGuide.endpoint.exportAssetsPublicPost.parameterDescription.cursor"),
                },
            ],
            [
                {
                    name: "filters",
                    required: false,
                    description: t("Support.apiGuide.endpoint.exportAssetsPublicPost.parameterDescription.filters"),
                },
                {
                    name: "paths",
                    required: false,
                    description: t("Support.apiGuide.endpoint.exportAssetsPublicPost.parameterDescription.paths"),
                },
                {
                    name: "sort_column",
                    required: false,
                    description: t("Support.apiGuide.endpoint.exportAssetsPublicPost.parameterDescription.sortColumn"),
                },
                {
                    name: "sort_column_type",
                    required: false,
                    description: t(
                        "Support.apiGuide.endpoint.exportAssetsPublicPost.parameterDescription.sortColumnType"
                    ),
                },
                {
                    name: "sort_ascending",
                    required: false,
                    description: t(
                        "Support.apiGuide.endpoint.exportAssetsPublicPost.parameterDescription.sortAscending"
                    ),
                },
                {
                    name: "search",
                    required: false,
                    description: t("Support.apiGuide.endpoint.exportAssetsPublicPost.parameterDescription.search"),
                },
                {
                    name: "start_date",
                    required: false,
                    description: t("Support.apiGuide.endpoint.exportAssetsPublicPost.parameterDescription.startDate"),
                },
                {
                    name: "end_date",
                    required: false,
                    description: t("Support.apiGuide.endpoint.exportAssetsPublicPost.parameterDescription.endDate"),
                },
                {
                    name: "filter_logic",
                    required: false,
                    description: t("Support.apiGuide.endpoint.exportAssetsPublicPost.parameterDescription.filterLogic"),
                },
                {
                    name: "group_uuid",
                    required: false,
                    description: t("Support.apiGuide.endpoint.exportAssetsPublicPost.parameterDescription.groupUuid"),
                },
                {
                    name: "data_bearing_filter",
                    required: false,
                    description: t(
                        "Support.apiGuide.endpoint.exportAssetsPublicPost.parameterDescription.dataBearingFilter"
                    ),
                },
            ]
        ),
    };
}

function createFindAssetsEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const responseExample = {
        assets: [
            {
                uuid: "40530b3a-6c5f-46fa-b1b6-a3bfaebb7378",
                asset_id: "fffe468b-2319-464c-b412-900acc62b774",
                tenant_uuid: "a8f2ed18-867d-4d62-8cae-6fbe327b3547",
                asset_details: {
                    serial_number: "SN-GLC84AAEB1",
                    asset_type: "Laptop",
                    asset_state: "Under Repair",
                    manufacturer: "HP",
                    model: "EliteBook 840 G10",
                    asset_tag: "AT-361605",
                    assigned_to: "Woewjvqe Ukophzabt",
                    location: "Building C",
                    department: "Sales",
                    region: "Central",
                    notes: "Under warranty",
                    unknown_header: "SN-E74N008F26",
                },
            },
        ],
    };

    return {
        linkId: "find-assets-get",
        title: t("Support.apiGuide.endpoint.findAsset.title"),
        introduction: t("Support.apiGuide.endpoint.findAsset.introduction"),
        url: baseUrl,
        method: "GET",
        format: "application/json",
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(
                `${baseUrl}?assetId=fffe468b-2319-464c-b412-900acc62b774&serialNumber=SN-GLC84AAEB1`
            ),
        ]),
        parameterSection: createParameterSection(t, [
            {
                name: "assetId",
                required: false,
                description: t("Support.apiGuide.endpoint.findAsset.parameterDescription.assetId"),
            },
            {
                name: "serialNumber",
                required: false,
                description: t("Support.apiGuide.endpoint.findAsset.parameterDescription.serialNumber"),
            },
        ]),
        mainTitle: t("Support.apiGuide.paths.assetTypeTitle"),
        description: t("Support.apiGuide.paths.assetTypeDescription"),
    };
}

function createAssetPostEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const requestExample = {
        asset_id: "AST-001",
        serial_number: "SN-ABC123",
        asset_tag: "AT-12345",
        asset_type: "Laptop",
        asset_state: "Available",
        manufacturer: "HP",
        assigned_to: "jane.doe@example.com",
        location: "Building A",
        department: "Engineering",
        region: "North",
        asset_details: [
            { key: "warranty_status", value: "valid", source: "manual" },
            { key: "operating_system", value: "Windows", source: "manual" },
        ],
    };
    const responseExample = {
        asset_id: "AST-001",
    };
    return {
        linkId: "create-asset-post",
        title: t("Support.apiGuide.endpoint.createAsset.title"),
        introduction: t("Support.apiGuide.endpoint.createAsset.introduction"),
        url: baseUrl,
        method: "POST",
        format: "application/json",
        requestExample: requestExample,
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_JSON_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA),
            createJsonCommandPart(singleQuote(JSON.stringify(requestExample, null, 4))),
            createPlainTextCommandPart(baseUrl),
        ]),
        parameterSection: createParameterSection(
            t,
            [],
            [
                {
                    name: "asset_id",
                    required: true,
                    description: t("Support.apiGuide.endpoint.createAsset.parameterDescription.asset_id"),
                },
                {
                    name: "serial_number",
                    required: true,
                    description: t("Support.apiGuide.endpoint.createAsset.parameterDescription.serial_number"),
                },
                {
                    name: "asset_tag",
                    required: false,
                    description: t("Support.apiGuide.endpoint.createAsset.parameterDescription.asset_tag"),
                },
                {
                    name: "asset_type",
                    required: true,
                    description: t("Support.apiGuide.endpoint.createAsset.parameterDescription.asset_type"),
                },
                {
                    name: "asset_state",
                    required: false,
                    description: t("Support.apiGuide.endpoint.createAsset.parameterDescription.asset_state"),
                },
                {
                    name: "manufacturer",
                    required: false,
                    description: t("Support.apiGuide.endpoint.createAsset.parameterDescription.manufacturer"),
                },
                {
                    name: "assigned_to",
                    required: false,
                    description: t("Support.apiGuide.endpoint.createAsset.parameterDescription.assigned_to"),
                },
                {
                    name: "location",
                    required: false,
                    description: t("Support.apiGuide.endpoint.createAsset.parameterDescription.location"),
                },
                {
                    name: "department",
                    required: false,
                    description: t("Support.apiGuide.endpoint.createAsset.parameterDescription.department"),
                },
                {
                    name: "region",
                    required: false,
                    description: t("Support.apiGuide.endpoint.createAsset.parameterDescription.region"),
                },
                {
                    name: "asset_details",
                    required: false,
                    description: t("Support.apiGuide.endpoint.createAsset.parameterDescription.asset_details"),
                },
            ]
        ),
    };
}

function createDeleteAssetEndpoint(t: TFunction, baseUrl: string): Endpoint {
    return {
        linkId: "delete-asset",
        title: t("Support.apiGuide.endpoint.deleteAsset.title"),
        introduction: t("Support.apiGuide.endpoint.deleteAsset.introduction"),
        url: baseUrl,
        method: "DELETE",
        format: t("Common.na"),
        responseExample: <></>,
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XDELETE),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(baseUrl.replace("{uuid}", "40530b3a-6c5f-46fa-b1b6-a3bfaebb7378")),
        ]),
        parameterSection: createParameterSection(t, [
            {
                name: "uuid",
                required: true,
                description: t("Support.apiGuide.endpoint.deleteAsset.parameterDescription.uuid"),
            },
        ]),
    };
}

function createApplyPricingRulesEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const requestExample = {
        rules: [
            {
                uuid: "a1b2c3d4-0000-0000-0000-000000000001",
                manufacturer: "Apple",
                model: "iPhone 16 Pro",
                blancco_grade: "B",
                value: 689,
                uplift_values: ["Battery replacement"],
                action: "EDIT",
            },
            {
                manufacturer: "Samsung",
                model: "Galaxy S24",
                blancco_grade: "A",
                value: 750,
                action: "CREATE",
            },
            {
                uuid: "b2c3d4e5-0000-0000-0000-000000000002",
                manufacturer: "Apple",
                model: "iPhone 14",
                blancco_grade: "D",
                value: 319,
                action: "DELETE",
            },
        ],
    };
    return {
        linkId: "apply-pricing-rules",
        title: t("Support.apiGuide.endpoint.applyPricingRules.title"),
        introduction: t("Support.apiGuide.endpoint.applyPricingRules.introduction"),
        url: baseUrl,
        method: "POST",
        format: "application/json",
        requestExample: requestExample,
        responseExample: <></>,
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_JSON_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA),
            createJsonCommandPart(singleQuote(JSON.stringify(requestExample, null, 4))),
            createPlainTextCommandPart(baseUrl),
        ]),
        parameterSection: createParameterSection(
            t,
            [],
            [
                {
                    name: "rules",
                    required: true,
                    description: t("Support.apiGuide.endpoint.applyPricingRules.parameterDescription.rules"),
                },
                {
                    name: "uuid",
                    required: false,
                    description: t("Support.apiGuide.endpoint.applyPricingRules.parameterDescription.uuid"),
                },
                {
                    name: "manufacturer",
                    required: true,
                    description: t("Support.apiGuide.endpoint.applyPricingRules.parameterDescription.manufacturer"),
                },
                {
                    name: "model",
                    required: true,
                    description: t("Support.apiGuide.endpoint.applyPricingRules.parameterDescription.model"),
                },
                {
                    name: "blancco_grade",
                    required: false,
                    description: t("Support.apiGuide.endpoint.applyPricingRules.parameterDescription.grade"),
                },
                {
                    name: "value",
                    required: true,
                    description: t("Support.apiGuide.endpoint.applyPricingRules.parameterDescription.value"),
                },
                {
                    name: "uplift_values",
                    required: false,
                    description: t("Support.apiGuide.endpoint.applyPricingRules.parameterDescription.upliftValue"),
                },
                {
                    name: "action",
                    required: true,
                    description: t("Support.apiGuide.endpoint.applyPricingRules.parameterDescription.action"),
                },
            ]
        ),
    };
}

function createFetchUpliftRulesEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const responseExample = {
        rules: [
            {
                uuid: "a1b2c3d4-0000-0000-0000-000000000001",
                headline: "Battery replacement",
                description: "Replace the battery with a new one",
                value: 25.5,
            },
        ],
        current_page: 1,
        total_pages: 1,
        total_rules: 1,
    };
    return {
        linkId: "fetch-uplift-rules-get",
        title: t("Support.apiGuide.endpoint.fetchUpliftRules.title"),
        introduction: t("Support.apiGuide.endpoint.fetchUpliftRules.introduction"),
        url: baseUrl,
        method: "GET",
        format: "application/json",
        responseExample: createJsonExample(t("Support.apiGuide.response"), responseExample, true),
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(`${baseUrl}?page=1&size=100`),
        ]),
        parameterSection: createParameterSection(t, [
            {
                name: "page",
                required: false,
                description: t("Support.apiGuide.endpoint.fetchUpliftRules.parameterDescription.page"),
            },
            {
                name: "size",
                required: false,
                description: t("Support.apiGuide.endpoint.fetchUpliftRules.parameterDescription.size"),
            },
            {
                name: "search",
                required: false,
                description: t("Support.apiGuide.endpoint.fetchUpliftRules.parameterDescription.search"),
            },
        ]),
    };
}

function createApplyUpliftRulesEndpoint(t: TFunction, baseUrl: string): Endpoint {
    const requestExample = {
        rules: [
            {
                headline: "Battery replacement",
                description: "Replace the battery with a new one",
                value: 25.5,
                action: "CREATE",
            },
            {
                uuid: "a1b2c3d4-0000-0000-0000-000000000001",
                value: 30,
                action: "EDIT",
            },
            {
                uuid: "b2c3d4e5-0000-0000-0000-000000000002",
                action: "DELETE",
            },
        ],
    };
    return {
        linkId: "apply-uplift-rules",
        title: t("Support.apiGuide.endpoint.applyUpliftRules.title"),
        introduction: t("Support.apiGuide.endpoint.applyUpliftRules.introduction"),
        url: baseUrl,
        method: "POST",
        format: "application/json",
        requestExample: requestExample,
        responseExample: <></>,
        curlSection: createCurlSection(t, [
            createPlainTextCommandPart(COMMAND_PART_CURL, false),
            createPlainTextCommandPart(COMMAND_PART_XPOST),
            createPlainTextCommandPart(CONTENT_TYPE_JSON_HEADER),
            createPlainTextCommandPart(API_KEY_HEADER),
            createPlainTextCommandPart(COMMAND_PART_DATA),
            createJsonCommandPart(singleQuote(JSON.stringify(requestExample, null, 4))),
            createPlainTextCommandPart(baseUrl),
        ]),
        parameterSection: createParameterSection(
            t,
            [],
            [
                {
                    name: "rules",
                    required: true,
                    description: t("Support.apiGuide.endpoint.applyUpliftRules.parameterDescription.rules"),
                },
                {
                    name: "uuid",
                    required: false,
                    description: t("Support.apiGuide.endpoint.applyUpliftRules.parameterDescription.uuid"),
                },
                {
                    name: "headline",
                    required: false,
                    description: t("Support.apiGuide.endpoint.applyUpliftRules.parameterDescription.headline"),
                },
                {
                    name: "description",
                    required: false,
                    description: t("Support.apiGuide.endpoint.applyUpliftRules.parameterDescription.description"),
                },
                {
                    name: "value",
                    required: false,
                    description: t("Support.apiGuide.endpoint.applyUpliftRules.parameterDescription.value"),
                },
                {
                    name: "action",
                    required: true,
                    description: t("Support.apiGuide.endpoint.applyUpliftRules.parameterDescription.action"),
                },
            ]
        ),
    };
}

export default function EndpointsView(): JSX.Element {
    const { t } = useTranslation();
    const reportExportUrl = getPublicApiUrl() + "/reports/export";
    const reportPublicLinksUrl = getPublicApiUrl() + "/reports/public-links";
    const reportCompareUrl = getPublicApiUrl() + "/reports/{uuid}/compare";
    const workflowsFetchUrl = getPublicApiUrl() + "/workflows";
    const workflowImportUrl = getPublicApiUrl() + "/workflows/import";
    const workflowExportUrl = getPublicApiUrl() + "/workflows/{uuid}/export";
    const workflowLaunchUrl = getPublicApiUrl() + "/workflows/{uuid}/launch";
    const workflowExecutionStateUrl = getPublicApiUrl() + "/workflows/{uuid}/executions/{contextId}/state";
    const singleReportExportUrl = getPublicApiUrl() + "/reports/{uuid}/export";
    const userCreationUrl = getPublicApiUrl() + "/users";
    const userEditUrl = getPublicApiUrl() + "/users/{uuid}";
    const roleCreationUrl = getPublicApiUrl() + "/roles";
    const singleRoleUrl = roleCreationUrl + "/{role_uuid}";
    const licenseDeliveryUrl = getPublicApiUrl() + "/license-deliveries";
    const tenantManagementBaseUrl = getPublicApiUrl() + "/tenants";
    const fetchTenantUrl = tenantManagementBaseUrl + "/{uuid}";
    const fetchTenantsLicensesUrl = getPublicApiUrl() + "/licenses";
    const fetchTenantsEsgDataUrl = getPublicApiUrl() + "/sustainabilities";
    const fetchTenantsLicenseEvents = getPublicApiUrl() + "/license-events";
    const reportImportUrl = getPublicApiUrl() + "/reports/import";
    const autopilotRecheckUrl = getPublicApiUrl() + "/autopilot/recheck";
    const resourcesUrl = getPublicApiUrl() + "/resources";
    const licenseKeyUrl = getPublicApiUrl() + "/license-keys";
    const auditLogsExportUrl = getPublicApiUrl() + "/audit-logs/export";
    const aboutUrl = getPublicApiUrl() + "/about";
    const assetsUrl = getPublicApiUrl() + "/assets";
    const deleteAssetUrl = assetsUrl + "/{uuid}";
    const pricingRulesUrl = getPublicApiUrl() + "/pricing-rules";
    const upliftRulesUrl = getPublicApiUrl() + "/uplift-rules";
    const cursorParameter = {
        name: "cursor",
        required: false,
        description: t("Support.apiGuide.endpoint.exportReportsPost.parameterDescription.cursor"),
    };
    const reportTypeEndpoints = [
        createExportReportWithUuidEndpoint(t, singleReportExportUrl),
        createExportReportGetEndpoint(t, reportExportUrl, cursorParameter),
        createExportReportPostEndpoint(t, reportExportUrl, cursorParameter),
        createReportPublicLinksEndpoint(t, reportPublicLinksUrl),
        createImportReportsPublicApiEndpoint(t, reportImportUrl),
        createAutopilotRecheckEndpoint(t, autopilotRecheckUrl),
        createCompareReportsEndpoint(t, reportCompareUrl),
    ];

    const userGroupsUrl = getPublicApiUrl() + "/user-groups";
    const userGroupUrl = userGroupsUrl + "/{group_uuid}";
    const userAndRolesTypeEndpoints = [
        createUserCreationPublicApiEndpoint(t, userCreationUrl),
        createUserEditPublicApiEndpoint(t, userEditUrl),
        createFetchUsersEndpoint(t, userCreationUrl),
        createFetchUserEndpoint(t, userEditUrl),
        createDeleteUserEndpoint(t, userEditUrl),
        createRoleCreationPublicApiEndpoint(t, roleCreationUrl),
        createFetchRolePublicApiEndpoint(t, roleCreationUrl),
        createFetchRoleEndpoint(t, singleRoleUrl),
        createEditRoleEndpoint(t, singleRoleUrl),
        createDeleteRoleEndpoint(t, singleRoleUrl),
        createCreateUserGroupEndpoint(t, userGroupsUrl),
        createEditUserGroupEndpoint(t, userGroupUrl),
        createFetchUserGroupsEndpoint(t, userGroupsUrl),
        createFetchUserGroupEndpoint(t, userGroupUrl),
        createDeleteUserGroupEndpoint(t, userGroupUrl),
    ];

    const tenantTypeEndpoints = [
        createFetchTenantsPublicApiEndpoint(t, tenantManagementBaseUrl),
        createFetchTenantPublicApiEndpoint(t, fetchTenantUrl),
        createTenantCreationPublicApiEndpoint(t, tenantManagementBaseUrl),
        createTenantEditPublicApiEndpoint(t, fetchTenantUrl),
        createFetchTenantsEsgDataPublicApiEndpoint(t, fetchTenantsEsgDataUrl),
    ];

    const licenseTypeEndpoints = [
        createLicenseDeliveryPublicApiEndpoint(t, licenseDeliveryUrl),
        createFetchTenantsLicensesPublicApiEndpoint(t, fetchTenantsLicensesUrl),
        createFetchTenantsLicenseEventsPublicApiEndpoint(t, fetchTenantsLicenseEvents),
    ];
    licenseTypeEndpoints.push(createLicenseKeyCreationEndpoint(t, licenseKeyUrl));

    const workflowTypeEndpoints = [
        createImportWorkflowPostEndpoint(t, workflowImportUrl),
        createExportWorkflowPostEndpoint(t, workflowExportUrl),
        createFetchAllWorkflowPostEndpoint(t, workflowsFetchUrl),
    ];
    const auditLogTypeEndpoints = [createAuditLogsExportEndpoint(t, auditLogsExportUrl)];
    const aboutTypeEndpoints = [createAboutEndpoint(t, aboutUrl)];
    workflowTypeEndpoints.push(createLaunchWorkflowPostEndpoint(t, workflowLaunchUrl));
    workflowTypeEndpoints.push(createFetchWorkflowExecutionStateEndpoint(t, workflowExecutionStateUrl));
    const assetTypeEndpoints = [];
    if (useFeature(FLAG_ASSET_MANAGEMENT)) {
        assetTypeEndpoints.push(createFindAssetsEndpoint(t, assetsUrl));
        assetTypeEndpoints.push(createAssetExportPublicApiEndpoint(t, assetsUrl + "/export"));
        assetTypeEndpoints.push(createAssetPostEndpoint(t, assetsUrl));
        assetTypeEndpoints.push(createDeleteAssetEndpoint(t, deleteAssetUrl));
        assetTypeEndpoints.push(createApplyPricingRulesEndpoint(t, pricingRulesUrl));
        assetTypeEndpoints.push(createFetchUpliftRulesEndpoint(t, upliftRulesUrl));
        assetTypeEndpoints.push(createApplyUpliftRulesEndpoint(t, upliftRulesUrl));
    }
    const tenantNameAvailabilityEndpoint = createTenantNameAvailabilityEndpoint(
        isUserParentInternal(),
        t,
        resourcesUrl + "/tenants"
    );
    if (tenantNameAvailabilityEndpoint) {
        tenantTypeEndpoints.push(tenantNameAvailabilityEndpoint);
    }
    const usernameAvailabilityEndpoint = createUsernameAvailabilityEndpoint(
        isUserParentInternal(),
        t,
        resourcesUrl + "/users"
    );
    if (usernameAvailabilityEndpoint) {
        userAndRolesTypeEndpoints.push(usernameAvailabilityEndpoint);
    }
    function CollapsibleBox({ title, method, internal, children }: CollapsibleBoxProps) {
        return (
            <details
                className={style.collapsibleBox}
                data-testid={testIds.workArea.support.apiGuide.endpoint.collapsible}
            >
                <summary className={style.collapsibleHeader}>
                    <div className={style.collapsibleTitle}>
                        <Heading tag="div" variant="SUBTITLE_1" className={style.noBorder}>
                            <Box gap="Sm" verticalAlignment="Center">
                                <Box gap="Xxs" verticalAlignment="Center">
                                    {method === "GET" && (
                                        <ApiMethodBadge
                                            text="GET"
                                            testId={testIds.workArea.support.apiGuide.endpoint.methodIcon}
                                            type="GET"
                                        />
                                    )}
                                    {method === "POST" && (
                                        <ApiMethodBadge
                                            text="POST"
                                            testId={testIds.workArea.support.apiGuide.endpoint.methodIcon}
                                            type="POST"
                                        />
                                    )}
                                    {method === "DELETE" && (
                                        <ApiMethodBadge
                                            text="DELETE"
                                            testId={testIds.workArea.support.apiGuide.endpoint.methodIcon}
                                            type="DELETE"
                                        />
                                    )}
                                    {internal && <StatusBadge value={t("Common.internal")} status={4} />}
                                </Box>
                                {title}
                            </Box>
                        </Heading>
                    </div>
                </summary>
                <div className={style.collapsibleContent}>{children}</div>
            </details>
        );
    }

    function groupSimilarEndpoints(endpoint: Endpoint[]) {
        return endpoint.map((each) => (
            <div key={each.linkId} id={each.linkId} data-testid={testIds.workArea.support.apiGuide.endpoint.itself}>
                <div className={style.collapsibleTitle}>
                    {each.mainTitle && (
                        <Heading tag="div" variant="SUBTITLE_1">
                            {each.mainTitle}
                        </Heading>
                    )}
                </div>
                <div className={style.collapsibleDescription}>
                    {each.description && <TextBlock>{each.description}</TextBlock>}
                </div>
                <CollapsibleBox title={each.title} method={each.method} internal={each.internal}>
                    <div className={style.twoColumnLayout}>
                        <div className={style.leftColumn}>
                            <div className={endpointStyle.textWidth}>
                                <TextBlock>{each.introduction}</TextBlock>
                            </div>
                            {!each.requestExample && createUrlSection(t, each)}
                            {each.requestExample && <div className={style.textWidth}>{createUrlSection(t, each)}</div>}
                            {each.details && <div className={style.textWidth}>{each.details}</div>}
                            {each.parameterSection}
                        </div>
                        <div className={style.rightColumn}>
                            {createBodyExamples(t, each)}
                            {each.curlSection}
                        </div>
                    </div>
                </CollapsibleBox>
            </div>
        ));
    }

    const reportTypeEndpointSections = groupSimilarEndpoints(reportTypeEndpoints);
    const userAndRolesTypeEndpointSection = groupSimilarEndpoints(userAndRolesTypeEndpoints);
    const tenantTypeEndpointSection = groupSimilarEndpoints(tenantTypeEndpoints);
    const licenseTypeEndpointSection = groupSimilarEndpoints(licenseTypeEndpoints);
    const workflowTypeEndpointSection = groupSimilarEndpoints(workflowTypeEndpoints);
    const auditLogTypeEndpointSection = groupSimilarEndpoints(auditLogTypeEndpoints);
    const aboutTypeEndpointSection = groupSimilarEndpoints(aboutTypeEndpoints);
    const assetTypeEndpointsSection = groupSimilarEndpoints(assetTypeEndpoints);

    React.useEffect(() => {
        // It's really difficult to place the copy icon to the upper right corner of the textarea while also preparing
        // for possible scrollbars. Here the issue is solved by eliminating the scrollbars completely and always
        // resizing the textarea to fit the content. Given that the textareas aren't always visible when the page loads,
        // we need to do this in useEffect.
        const areas = document.querySelectorAll('textarea[data-resize="true"]');
        for (let i = 0; i < areas.length; i++) {
            areas[i].setAttribute("style", "height:" + areas[i].scrollHeight + "px");
        }
    });
    return (
        <div>
            <div>
                <Heading tag="div" variant="SUBTITLE_1">
                    {t("Support.apiGuide.endpointResources")}
                </Heading>
            </div>
            <div className={style.mainBoxLayout}>{reportTypeEndpointSections}</div>
            <div className={style.mainBoxLayout}>{userAndRolesTypeEndpointSection}</div>
            <div className={style.mainBoxLayout}>{tenantTypeEndpointSection}</div>
            <div className={style.mainBoxLayout}>{licenseTypeEndpointSection}</div>
            <div className={style.mainBoxLayout}>{workflowTypeEndpointSection}</div>
            <div className={style.mainBoxLayout}>{auditLogTypeEndpointSection}</div>
            <div className={style.mainBoxLayout}>{aboutTypeEndpointSection}</div>
            <div className={style.mainBoxLayout}>{assetTypeEndpointsSection}</div>
        </div>
    );
}
